import { useCallback, useEffect, useMemo, useState } from 'react'
import { lagosDaysAgo, lagosToday, methodLabel, naira, paymentSummary, roomChargeDept, startingDept, tierLabel, whoRecorded } from '../lib/format'
import { checkRepeatSale, loadStockMap, loadPopular, loadToday, saveBasket, saveWriteoff,
         loadDailyFinancials, loadCustomers, createCustomer,
         loadReceptionRoomSales, loadRoomDebtRecovered, withRoomNights, NIGHTS_AS_SALES_FROM,
         loadOpeningDate, loadBalances, loadReceipt,
         loadStaffForLocation, loadReceptionActivity, loadReceptionDashboard, deleteEntry,
         loadRoomCharges, loadRoomBillHandover, deleteOrderItem, saveRestaurantWriteoff, loadRoomsSoldInMonth,
         loadWriteoffsOnDate, loadLiveStays, chargeBasketToRoom,
         getOrCreateGuestTab, updateSaleWithPayments, undoRecentReceipt, recordTillCheck, tillCheckDone } from '../lib/data'
import { enqueue, flush, isConnectionError } from '../lib/outbox'
import { useToast } from '../components/Toast'
import ReceptionDashboard from '../components/ReceptionDashboard'
import WriteoffBreakdown from '../components/WriteoffBreakdown'
import RoomChargeApprovals from '../components/RoomChargeApprovals'
import CollectRefusedCharges from '../components/CollectRefusedCharges'
import MyPendingRoomCharges from '../components/MyPendingRoomCharges'
import RoomChargeTracker from '../components/RoomChargeTracker'
import StockUsedField from '../components/StockUsedField'
import ItemPicker from '../components/ItemPicker'
import RoomChargeSheet from '../components/RoomChargeSheet'
import CustomerPicker from '../components/CustomerPicker'
import Receipt from '../components/Receipt'
import MatchTill from '../components/MatchTill'
import TillTile, { SubLine, ByMethod } from '../components/TillTile'
import HandoverNotes from '../components/HandoverNotes'
import { APPROVE_ROOM_CHARGES, EDITOR, MANAGEMENT, OVERSIGHT, SUPERVISOR, is } from '../lib/roles'
import Notice from '../components/Notice'
import { tracked } from '../lib/loadfail'

const niceDay = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })

// Why a sale is being dated to a day that is not today or yesterday.
//
// 19 October 2026, Chris's design: chips, with typing required only on
// Other. A free-text box asked at the end of a long entry gets "y" — a
// chip takes one tap and still says something true.
//
// These are reasons a SALE is late, not reasons stock moved. Breakage,
// expired, theft, short delivery and the rest belong to a count, where
// they already are; none of them explains why a sale carries Saturday's
// date. A list that does not fit what is being recorded gets answered
// at random, which is the same as not asking.
const LATE_SALE_REASONS = [
  'Not keyed on the day',
  'No network',
  'Closing an old day',
  'Found in the written record',
  'Wrong date first time',
]

// Named ...Field deliberately: it is the control that answers the
// refusal, and tools/check-guards.mjs looks for a control in the same
// sheet as the guard that demands it.
function BackdateReasonField({ value, onChange, other, onOther }) {
  const chip = (active) => `h-10 px-3 rounded-full border text-sm font-semibold ${active
    ? 'bg-amber text-bg border-amber' : 'border-line text-dim'}`
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {LATE_SALE_REASONS.map(r => (
          <button key={r} type="button"
            onClick={() => { onOther(false); onChange(value === r ? '' : r) }}
            className={chip(!other && value === r)}>
            {r}
          </button>
        ))}
        <button type="button"
          onClick={() => { onOther(true); onChange('') }}
          className={chip(other)}>
          Other
        </button>
      </div>
      {other && (
        <input value={value} autoFocus
          onChange={e => onChange(e.target.value)}
          placeholder="Say what happened (required)"
          className={`mt-2 h-12 w-full px-3 rounded-xl bg-raise border ${
            value.trim() ? 'border-line' : 'border-amber'}`} />
      )}
    </div>
  )
}

// What should be in hand right now: the same three figures Match your
// Total Sales expects, shown on the dashboard so the two can never
// disagree. Module-level so typing elsewhere on the page cannot remount it.
// Held back until the count is saved, for the person whose count it is.
//
// Every till check ever recorded, at both branches, came out equal to
// the penny on both POS and cash. A check that cannot disagree measures
// nothing, which is why Match your Total Sales hides its own figure
// until you have typed yours — and printing the same figure on a tile
// directly above it would hand straight back what that hides.
//
// The first attempt at this showed three dashes and a grey line of
// explanation, and read as a page that had failed to load. So the
// waiting state now says what it is before it says anything else: a
// heading, an amber band naming the one thing to do, and dots where the
// figures will land. Hidden has to look deliberate, or it looks broken.

export default function SalesEntry({ boot }) {
  const { staff, locations, allLocations, tiers, methods, items } = boot
  const approvalsOn = boot.roomChargeApprovalLimit != null
  const toast = useToast()
  // Manager/gm/admin reach this page via More's "Record a sale for
  // any department" — that phrase is the actual product intent, so
  // they need every department as an option, not just whatever their
  // own staff_locations row happens to include (which may be none,
  // since oversight roles aren't normally tied to one department).
  // Bar/front_desk/storekeeper, who use this as their direct working
  // tab, stay scoped to their own assigned department(s) as before.
  // OVERSIGHT, matching Credit and Recovery, so the name means the same
  // thing on every page. NOTE: on THIS page department chips decide
  // where a sale is RECORDED, so this also lets store managers ring up
  // sales at any department — not only view them. Auditors cannot reach
  // this page, so for them nothing changes.
  const seesAllDepartments = is(staff.role, OVERSIGHT)
  const salesPoints = (seesAllDepartments ? allLocations : locations).filter(l => l.is_sales_point && !l.is_store)
  // anyone who records a sale may date it; overriding an unbalanced
  // sale stays with the roles above bar staff
  const canBackdate = staff.role !== 'auditor'
  const canOverrideVariance = is(staff.role, EDITOR)
  // Specifically excluded from on-behalf-of, per policy — pinned to
  // their staff id so a role change doesn't quietly reopen it, and
  // Nnewi's storekeeper (or any future Awka one) is unaffected. The
  // database enforces the same block; this only hides the picker so
  // they never see a control that would fail server-side.
  const NO_ON_BEHALF = new Set(['a5ea88b6-80e7-4776-a491-78a509e589c6'])
  const canRecordOnBehalf = canOverrideVariance && !NO_ON_BEHALF.has(staff.id)
  // bar staff can reach back 4 days; editors go to the opening balance
  // Must match window_days in validate_sale_date (migration 284).
  // If these drift, staff are offered dates the database refuses.
  const STAFF_BACKDATE_DAYS = 7
  const todayDate = lagosToday()
  const [date, setDate] = useState(todayDate)
  const [openingDate, setOpeningDate] = useState(null)
  const earliestDate = (() => {
    if (canOverrideVariance) return openingDate || undefined
    // count back from the Lagos business date so the boundary does not
    // shift with the device's timezone
    const d = new Date(todayDate + 'T12:00:00')
    d.setDate(d.getDate() - STAFF_BACKDATE_DAYS)
    const floor = d.toISOString().slice(0, 10)
    return openingDate && openingDate > floor ? openingDate : floor
  })()
  const [backdateReason, setBackdateReason] = useState('')
  // Whether Other is the chosen chip. The reason itself is the one
  // string that gets stored either way; this only says which control
  // is showing.
  const [reasonOther, setReasonOther] = useState(false)
  // The page's date is a SETTING, not a field on an entry: set once, it
  // governs everything recorded until it is changed. So it cannot be
  // guarded the way the folio's date is — by sitting last on the form —
  // because by the tenth entry it is off the top of the screen. It is
  // carried instead: on a band that stays put, on the button that
  // commits, and in the message that confirms (18 October 2026).
  const offToday = date !== todayDate
  // Closing yesterday's account this morning is how this hotel works,
  // not an exception to it (18 October 2026). Thirty days of entries:
  // 1,568 sales keyed the day after the one they belong to against 332
  // keyed the same day, and over half of those between 09:00 and 11:00.
  // Demanding a typed reason for the normal way of working would have
  // asked for one on four sales in every five — and a reason given
  // sixty times a morning is "y", which protects nothing.
  //
  // So yesterday passes without being questioned; the day is still
  // named on screen, because keying into the wrong day is the mistake
  // that matters. Two days back or more is genuinely out of the
  // ordinary — 138 sales in thirty days — and still has to say why.
  const yesterdayDate = lagosDaysAgo(1)
  const needsReason = offToday && date < yesterdayDate
  const dayShort = (d) => new Date(d + 'T12:00:00')
    .toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })

  const [locationId, setLocationId] = useState(startingDept(salesPoints, staff, locations))
  // SEEING a department and RECORDING at it are separate. Store managers
  // and auditors may select any department to view it (OVERSIGHT, above),
  // but may only record at departments they are assigned to. Manager, gm
  // and admin keep recording anywhere, exactly as before.
  const ownLocationIds = new Set((locations || []).map(l => l.id))
  const canRecordAt = (id) => is(staff.role, MANAGEMENT) || ownLocationIds.has(id)
  // Whose till this actually is — not who is allowed to record at it.
  //
  // canRecordAt is true at EVERY department for a manager, gm or admin,
  // so using it to cover the POS/Cash/Credit tile told an auditor or a
  // manager to go and count a till that is not theirs. Only the person
  // the count belongs to is asked for it; everyone else sees the
  // figures, and once the count is saved so does everyone.
  const isMyTill = ownLocationIds.has(locationId)
  const canRecordHere = canRecordAt(locationId)
  // Mirrors moves_write after 283: anyone who can record, except an
  // auditor. Deliberately NOT canOverrideVariance, which also governs
  // backdating and variance overrides — different powers.
  const canWriteOff = (staff.role !== 'auditor' || !!staff.can_record_sales) && canRecordHere
  const canApprove = is(staff.role, APPROVE_ROOM_CHARGES)
  // Restaurant and Reception don't behave like a normal sales
  // department — Restaurant is typed-order-only (no catalog stock to
  // sell), Reception doesn't record sales at all (check-in/checkout/
  // folio work lives on the Rooms tab instead). Computed once here so
  // every button below agrees on it, rather than three separate
  // checks that could drift apart.
  const currentDept = salesPoints.find(l => l.id === locationId)
  const isRestaurant = /restaurant/i.test(currentDept?.name || '')
  const isReception = /reception/i.test(currentDept?.name || '')
  // Only the front desk puts anything on a guest's room bill
  // (17 October 2026, Chris's decision after a week of chasing money
  // through room bills). Restaurant, MainBar and Minimart went first,
  // OpenBar the same day: every department now handles its own sales —
  // payment at the point of sale, or a tab in the guest's own name
  // which the department collects itself.
  //
  // Reception is the one exception and must stay one: the room rate and
  // anything ordered through the front desk is what a folio IS. Written
  // as "Reception only" rather than a list of the four, so a department
  // added later cannot quietly inherit the room bill by not being on a
  // list someone forgot to update.
  //
  // Nothing already charged is touched. Existing charges stay on their
  // folios, still settle at checkout, and still count as that
  // department's credit until they are paid.
  const canChargeToRoom = isReception
  // Which order_items.category a room charge from THIS department
  // lands under — null for Reception, which has no charges of its
  // own. Any department can have items charged to a room, not just
  // Restaurant, so this drives loading and merging generically
  // rather than special-casing one department.
  //
  // No longer a CATEGORY. Category could not tell MainBar from OpenBar —
  // both are 'drink' — so each bar was shown the other's charges as its
  // own. The department is resolved per line by roomChargeDept instead
  // (migration 503).
  // Re-sync selected department on branch switch (GM) — otherwise the
  // old branch's location id stays selected, matching no chip here,
  // so nothing highlights until a manual tap.
  useEffect(() => {
    const valid = salesPoints.some(l => l.id === locationId)
    if (!valid) setLocationId(startingDept(salesPoints, staff, locations))
  }, [staff.branch_id])
  const [stockMap, setStockMap] = useState({})
  const [popular, setPopular] = useState({})
  const [today, setToday] = useState([])
  const [roomCharges, setRoomCharges] = useState([])   // Restaurant only: food charged to rooms
  const [receptionActivity, setReceptionActivity] = useState([])
  const [receptionDashboard, setReceptionDashboard] = useState(null)
  // Room payments taken through THIS department's POS/cash (migration 336).
  // Money this department collected from guests against their room bills.
  // It is real money in the drawer here, so it belongs in the day's income —
  // the Match-your-Total-Sales box has always counted it; the summary below
  // it did not, which is why a department's figures looked short.
  // Ticked room-bill money, by department (migration 505).
  const [handover, setHandover] = useState([])
  const [lastReceiptAt, setLastReceiptAt] = useState(0)
  const [switchingId, setSwitchingId] = useState(null)
  const [roomsSold, setRoomsSold] = useState(null)
  const [writeoffs, setWriteoffs] = useState(null)
  // In-house guests offered at the credit step, so a guest's drinks go
  // to their room or their own linked tab instead of a free-floating
  // customer account named after the room.
  const [inHouse, setInHouse] = useState([])
  const [guestChoice, setGuestChoice] = useState(null)  // stay awaiting room-vs-tab
  const [roomGuard, setRoomGuard] = useState(null)      // typed name that looks like a room
  // Internal rooms (GM Office, via rooms.is_internal) and the
  // monthly rooms-sold count are both
  // GM/admin-only visibility on the reception dashboard — matches
  // is_supervisor()'s own role set, not the broader oversight group.
  const isGmOrAdmin = is(staff.role, SUPERVISOR)
  const [showYesterday, setShowYesterday] = useState(false)
  const [yesterdaySummary, setYesterdaySummary] = useState(null)
  const [yesterdayActivity, setYesterdayActivity] = useState(null)
  // GM/Admin-only cleanup for training records, scoped specifically to
  // Restaurant here (Reception's version deletes a whole booking, not
  // a single row, so it lives separately in Folio)
  const canDeleteTraining = isRestaurant && is(staff.role, SUPERVISOR)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [writeoffBusy, setWriteoffBusy] = useState(false)

  async function doDeleteEntry() {
    setDeleteBusy(true)
    try {
      if (deleteConfirm.entryKind === 'roomCharge') {
        await deleteOrderItem(deleteConfirm.id, deleteConfirm.order_id)
      } else {
        await deleteEntry({ kind: 'sale', id: deleteConfirm.id })
      }
      toast('Deleted', 'success')
      setDeleteConfirm(null); refresh()
    } catch (e) { toast('Could not delete: ' + e.message, 'error') }
    setDeleteBusy(false)
  }
  const [summary, setSummary] = useState(null)
  const [recon, setRecon] = useState(null)
  const [customers, setCustomers] = useState([])

  const [basket, setBasket] = useState([])          // [{ key, item, tier, qty, unitPrice }]
  const [defaultTier, setDefaultTier] = useState('general')
  const [writeoffMode, setWriteoffMode] = useState(false)
  const [onBehalfOf, setOnBehalfOf] = useState(null)
  const [people, setPeople] = useState([])
  const [picking, setPicking] = useState(false)
  const [tuning, setTuning] = useState(null)        // line being adjusted
  const [paying, setPaying] = useState(null)        // payment step
  // An identical line on the same account within the last two minutes,
  // held up for confirmation.
  const [repeatSale, setRepeatSale] = useState(null)
  useEffect(() => { if (!paying) setRepeatSale(null) }, [paying])
  const [writeoff, setWriteoff] = useState(null)    // separate PR/damage flow
  const [busy, setBusy] = useState(false)
  const [receipt, setReceipt] = useState(null)
  const [lastReceiptId, setLastReceiptId] = useState(null)
  const [roomCharging, setRoomCharging] = useState(false)
  const [restaurantOrder, setRestaurantOrder] = useState(null)

  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items])
  // Restaurant food charged to a room never showed up here before —
  // it lives in orders/order_items, not sales, so it's merged in
  // specifically for this department rather than folded into
  // loadToday itself, which every other department still uses as-is.
  const combinedToday = useMemo(() => {
    const sales = today.map(r => ({ ...r, entryKind: 'sale', sortAt: r.created_at }))
    if (isReception) return sales
    // Only THIS department's charges. Previously filtered by category, so
    // every bar resolved to 'drink' and each one listed the other's
    // charges as its own (migration 503).
    const mine = roomCharges.filter(r =>
      roomChargeDept(r, boot.allLocations || locations) === locationId)
    const charges = mine.map(r => ({
      ...r, entryKind: 'roomCharge', sortAt: r.orders?.created_at,
      roomNumber: r.orders?.stays?.rooms?.room_number, guestName: r.orders?.stays?.guests?.full_name,
      chargedBy: r.orders?.served?.full_name,
    }))
    return [...sales, ...charges].sort((a, b) => (b.sortAt || '').localeCompare(a.sortAt || ''))
  }, [today, roomCharges, isReception, locationId, boot, locations])

  // What this department charged to rooms today. In the day's total at
  // the top of the page but NOT in gross sales — a room charge is not
  // money at this till — so without it on the card the two figures sit
  // on one screen with nothing explaining the difference.
  //
  // MUST stay below combinedToday: it reads it, and a const cannot be
  // read above its own declaration. Placed above it, the whole page
  // threw "Cannot access before initialization" on render.
  const roomChargedToday = combinedToday
    .filter(r => r.entryKind === 'roomCharge' && r.order_type !== 'pr_damage')
    .reduce((t, r) => t + Number(r.amount ?? r.qty * r.unit_price ?? 0), 0)

  // The part of it nobody has settled yet. Goods charged to a room and
  // not paid for are goods this department let go on trust, which is
  // what the Credit figure is for — the same thing as a credit sale,
  // owed by a guest instead of a customer.
  const roomUnpaidToday = combinedToday
    .filter(r => r.entryKind === 'roomCharge' && r.order_type !== 'pr_damage')
    .reduce((t, r) => t + Math.max(0,
      Number(r.amount ?? r.qty * r.unit_price ?? 0) - Number(r.paidOnLine || 0)), 0)

  const locById = useMemo(() =>
    Object.fromEntries((boot.allLocations || locations).map(l => [l.id, l])), [boot, locations])

  // Room-bill money the front desk collected for THIS department today, and
  // the whole of it, which is what Reception passes on. Split by how the
  // guest paid, because the two settle differently:
  //
  //   POS   the money is already in the bank on Reception's terminal, and
  //         the departments' shares are written on the one physical slip —
  //         so it belongs in each department's POS figure, not its drawer
  //   cash  actually changes hands at close, so it moves drawer to drawer
  //
  // Both sides move together. Crediting a department without debiting
  // Reception would just move the gap from one till to the other. The
  // ₦35,000 argument of 15 October was NOT this: Reception read ₦190,000
  // against a ₦155,000 slip because one payment had not been allocated
  // yet, so it was still being counted as Reception's own. Once it was,
  // the figure came to ₦155,000 on its own. The subtraction is right.
  //
  // Room bills are taken on POS or in cash only — the folio offers no other
  // method — so anything not cash is POS. Split because the money keeps its
  // form: a department's share of a POS payment is POS to them too.
  const splitBy = (rows) => rows.reduce((m, h) => {
    const k = h.method === 'cash' ? 'cash' : 'pos'
    m[k] += h.amount
    return m
  }, { pos: 0, cash: 0 })
  // What a department is owed from room bills no longer comes from here
  // — the day's own figures carry it as debt recovered (531). Reception
  // still needs the whole of it, because the departments' shares are on
  // its slip and in its drawer, and come off what it hands over.
  // Only what the FRONT DESK collected on the departments' behalf.
  //
  // 18 October 2026. Awka's 17th: nine card payments, ₦228,700. Six
  // were room rate at the front desk (₦175,000); the other three were
  // guests settling food and drink AT the Restaurant's and MainBar's
  // own tills (₦53,700). Reception's figure counts front-desk money
  // only — correctly — but this deduction counted every department
  // share from every payment, so ₦53,700 that was never in Reception's
  // slip was taken out of it anyway. The tile read ₦121,300 where the
  // front desk had ₦175,000 on the terminal.
  //
  // A department collecting at its own till keeps that money and is
  // credited with recovering the debt (531); Reception never holds it,
  // so Reception is never debited for it.
  const handedOut = splitBy(handover.filter(h => !h.paymentLocationId))
  const handedOutTotal = isReception ? handedOut.pos + handedOut.cash : 0
  // Say what is in the figure whenever it is more than the day's own
  // sales, so nobody has to guess why the terminal reads more than the
  // entries below add up to.
  const tileNote = (() => {
    const bits = [
      Number(recon?.recoveredFromCustomers || 0) > 0 && 'old debts paid today',
      Number(recon?.recoveredFromRooms || 0) > 0 && 'guests paying off what they charged to their rooms',
    ].filter(Boolean)
    if (isReception) {
      return handedOutTotal > 0
        ? `What should be in hand. Your whole slip and drawer, less ${naira(handedOutTotal)} you collected here on the departments' behalf.`
        : 'What should be in hand at the front desk.'
    }
    const credit = roomUnpaidToday > 0
      ? ` Credit includes ${naira(roomUnpaidToday)} given to guests' rooms and not yet paid.` : ''
    if (!bits.length) return `POS and Cash are what should be in hand — your sales for the day.${credit}`
    return `POS and Cash are what should be in hand — your sales plus ${bits.join(', plus ')}.${credit}`
  })()

  // ── What this department should physically have at close ───────────
  //
  // ONE calculation, used by the POS/Cash/Credit tile and by Match your
  // Total Sales. They were worked out separately before and disagreed:
  // the tile counted sales alone, so Reception — which records no sales
  // at all, only payments — showed ₦0 / ₦0 / ₦0 every day, and a bar
  // that recovered ₦3,000 of old debt by POS had it on the slip and not
  // in the tile.
  //
  //   POS     sales taken on the terminal, plus old debts paid by POS,
  //           plus room bills the front desk collected on your charges
  //   Cash    the same three, in the drawer
  //   Credit  what you GAVE today — not money, but it is why goods left
  //
  // Reception is the mirror image: its money is room payments rather
  // than sales, and the departments' shares, written on its one slip,
  // come off because they are not Reception's to account for.
  const r2 = n => Math.round(Number(n || 0) * 100) / 100
  const inHand = isReception
    ? { pos: r2((receptionDashboard?.pos || 0) - handedOut.pos),
        cash: r2((receptionDashboard?.cash || 0) - handedOut.cash),
        credit: null }
    // recoveredBy already carries the guests' room-bill money since 531
    // — adding handedToMe here as well would count it twice.
    : { pos: r2((summary?.byMethod?.pos || 0) + (recon?.recoveredBy?.pos || 0)),
        cash: r2((summary?.byMethod?.cash || 0) + (recon?.recoveredBy?.cash || 0)),
        // Everything that left on trust today: customer credit, plus
        // room charges still unsettled. POS and Cash are what is in
        // hand; Credit is the rest of what went out of the door.
        credit: r2((summary?.byMethod?.credit || 0) + roomUnpaidToday) }

  // Two figures at Reception (it gives no credit of its own), three
  // everywhere else.
  const tileFigures = [
    { label: 'POS', value: inHand.pos },
    { label: 'Cash', value: inHand.cash },
    ...(inHand.credit === null ? [] : [{ label: 'Credit', value: inHand.credit }]),
  ]

  async function openReceipt(receiptId) {
    // PR, damage, staff meals and room charges are not paid sales, so
    // they have no receipt. Say so plainly instead of reporting it as an
    // error, which made staff think something had gone wrong.
    if (!receiptId) {
      toast('This entry has no receipt — it was not a paid sale', 'info')
      return
    }
    try { setReceipt(await loadReceipt(receiptId)) }
    catch (e) { toast(e.message, 'error') }
  }

  const refresh = useCallback(() => {
    loadStockMap(staff.branch_id).then(setStockMap).catch(() => {})
    loadPopular(staff.branch_id).then(setPopular).catch(() => {})
    loadToday(staff.branch_id, date, locationId).then(setToday).catch(() => {})
    if (isReception) {
      loadReceptionActivity(staff.branch_id, date).then(setReceptionActivity).catch(() => {})
      loadReceptionDashboard(staff.branch_id, date).then(setReceptionDashboard).catch(() => {})
      if (isGmOrAdmin) {
        loadRoomsSoldInMonth(staff.branch_id, date).then(setRoomsSold).catch(() => {})
      }
    }
    if (!isReception) {
      loadRoomCharges(staff.branch_id, date).then(setRoomCharges).catch(() => {})
    }
    tracked('Money waiting from the front desk', loadRoomBillHandover(staff.branch_id, date)).then(setHandover).catch(() => setHandover([]))
    // Once this till has been counted for this day the figures stay
    // open. Without this the tile re-covered itself on every reload and
    // after every shift change, so the person who had already done the
    // work was asked for it again — which is how a deliberate lock
    // starts looking like a fault.
    tillCheckDone(staff.branch_id, locationId, date)
      .then(r => { setTillSaved(r); setTillCheckError(null) })
      .catch(e => { setTillSaved(false); setTillCheckError(e.message || 'could not check') })
    // Room-bill money reaches a department at the moment RECEPTION
    // records the guest's payment — and Reception records yesterday's
    // payments today, when closing yesterday's account. So a department
    // that counts before the front desk has finished is counting a
    // figure that is still moving underneath it. MainBar counted 16
    // October at 14:27 with ₦4,800 of room-bill money in; ₦4,900 more
    // landed that afternoon, and the cashier found the app short
    // against the slips. Nobody was wrong — the day moved after it was
    // shut. This is what lets the page say so before they count.
    const frontDesk = (boot.allLocations || locations).find(l => /reception/i.test(l.name))
    if (frontDesk && frontDesk.id !== locationId) {
      tracked('Whether this till has been counted', tillCheckDone(staff.branch_id, frontDesk.id, date))
        .then(setFrontDeskClosed).catch(() => setFrontDeskClosed(null))
    } else setFrontDeskClosed(null)
    if (locationId) {
      tracked('Whether your till has been counted', tillCheckDone(staff.branch_id, locationId, date))
        .then(setMyTillDone).catch(() => setMyTillDone(false))
    } else setMyTillDone(false)
    // On failure, record the error rather than silently showing nothing:
    // a hidden section looks identical to a day with no write-offs, which
    // is how a broken query went unnoticed.
    loadWriteoffsOnDate(staff.branch_id, date, locationId)
      .then(r => setWriteoffs({ ...r, error: null }))
      .catch(e => setWriteoffs({ pr: [], damage: [], food: [], prCost: 0, damageCost: 0,
                                 foodValue: 0, error: e.message || 'could not load' }))
    // Reception's own sales include the nights slept and paid for, from
    // 1 November (578) — the same figure DailySales shows, through the
    // same helper, so the two screens cannot drift apart.
    loadDailyFinancials(staff.branch_id, date, locationId).then(async f => {
      const useNights = isReception && date >= NIGHTS_AS_SALES_FROM
      const [nights, roomRec] = useNights
        ? await Promise.all([
            loadReceptionRoomSales(staff.branch_id, date).catch(() => null),
            loadRoomDebtRecovered(staff.branch_id, date).catch(() => null),
          ])
        : [null, null]
      const r = withRoomNights(f, nights, roomRec)
      setSummary({ byMethod: r.byMethod, nonRevenue: r.nonRevenue })
      setRecon({ roomNights: r.roomNights,
                 grossSales: r.grossSales, received: r.received, creditRaised: r.creditRaised,
                 debtRecovered: r.debtRecovered, recoveredBy: r.recoveredBy, totalMoneyIn: r.totalMoneyIn,
                 unqualifiedCredit: r.unqualifiedCredit,
                 recoveredFromCustomers: r.recoveredFromCustomers, recoveredFromRooms: r.recoveredFromRooms,
                 customerRecoveredBy: r.customerRecoveredBy, roomRecoveredBy: r.roomRecoveredBy,
                 debtClearedByPayroll: r.debtClearedByPayroll, debtWrittenOff: r.debtWrittenOff })
    }).catch(() => {})
    loadOpeningDate(staff.branch_id).then(setOpeningDate).catch(() => {})

    if (canOverrideVariance) {
      loadStaffForLocation(staff.branch_id, locationId).then(ps => {
        setPeople(ps)
        // a person selected while looking at the previous bar may not
        // work this one — drop the selection rather than leave it stale
        setOnBehalfOf(cur => ps.some(p => p.id === cur) ? cur : null)
      })
    } else {
      setPeople([])
    }

    const wantsBalances = methods.includes('credit')
    tracked('Customer accounts', Promise.all([
      loadCustomers(staff.branch_id),
      wantsBalances ? loadBalances(staff.branch_id, locationId).catch(() => []) : Promise.resolve([]),
    ]))
      .then(([cs, bals]) => {
        const byId = Object.fromEntries(bals.map(b => [b.customer_id, Number(b.balance)]))
        setCustomers(cs.map(c => ({ ...c, balance: byId[c.id] || 0 })))
      })
      .catch(() => setCustomers([]))
  }, [staff.branch_id, date, locationId, isReception])

  // Lazy — only fetched when actually asked for, not on every load.
  useEffect(() => {
    if (!isReception || !showYesterday) return
    const y = lagosDaysAgo(1)
    loadDailyFinancials(staff.branch_id, y, locationId).then(setYesterdaySummary).catch(() => {})
    loadReceptionActivity(staff.branch_id, y).then(setYesterdayActivity).catch(() => {})
  }, [isReception, showYesterday, staff.branch_id, locationId])
  useEffect(refresh, [refresh])

  // Nnewi prices a restaurant order by what came out of the kitchen
  // store; Awka still types a price per plate until its catalogue has
  // selling prices on every item (579).
  const priceFromStock = !!boot.restaurantPriceFromStock
  const stockValueOf = (used) => (used || []).reduce(
    (t, u) => t + Number(u.qty || 0) * Number(u.item?.selling_price || 0), 0)

  // FOOD THAT CARRIES NO PRICE (Chris, 31 October 2026).
  //
  // Awka's catalogue has items at zero — ingredients, and the staff and
  // GM meals. Priced from stock, an order made only of those comes to
  // nothing, and the save was simply refused, which stops the kitchen.
  //
  // Chris's rule: it is not a sale, it is PR. So it saves — but it can
  // never save as a SALE, and the screen says why. The alternative,
  // letting a zero total through as a standard order, would take a plate
  // a guest actually paid for out of Restaurant's takings with nothing
  // on screen to show it had happened.
  const noSaleValue = (o) => !!priceFromStock && (o?.used || []).length > 0
    && stockValueOf(o?.used) <= 0.009

  const priceFor = (item, tier) =>
    tier === 'lounge' ? Number(item.lounge_price ?? item.selling_price)
    : tier === 'staff' ? Number(item.staff_price ?? item.selling_price)
    : Number(item.selling_price)

  const onHand = (itemId, atLocationId) => stockMap[`${itemId}:${atLocationId || locationId}`] ?? 0
  const basketTotal = basket.reduce((s, l) => s + l.qty * l.unitPrice, 0)

  function addToBasket(item, sourceLocation) {
    setPicking(false)
    setBasket(b => {
      const at = b.findIndex(l => l.item?.id === item.id && l.tier === defaultTier
        && !l.priceOverridden && l.locationId === (sourceLocation?.id || undefined))
      if (at >= 0) {
        const copy = [...b]; copy[at] = { ...copy[at], qty: copy[at].qty + 1 }; return copy
      }
      return [...b, { key: crypto.randomUUID(), item, tier: defaultTier, qty: 1,
                      unitPrice: priceFor(item, defaultTier), priceOverridden: false,
                      // undefined for a normal same-department item (falls back to
                      // the basket's own locationId in saveBasket); set only for a
                      // cross-department pick like a Restaurant item sold at a bar
                      locationId: sourceLocation?.id, locationName: sourceLocation?.name }]
    })
  }

  // typed restaurant order — no catalog item, since a plate of food
  // isn't a countable stock unit the way a bottled drink is. Always
  // attributed to Restaurant's own daily figures regardless of which
  // bar rings it up, matching the earlier decision on how that
  // revenue should count.
  function addTypedOrder({ description, qty, unitPrice, orderType, writeoffNote, stockUsed }) {
    const restaurant = (boot.allLocations || []).find(l => /restaurant/i.test(l.name))
    setBasket(b => [...b, { key: crypto.randomUUID(), item: null, description,
                            tier: 'general', qty, unitPrice, priceOverridden: true,
                            locationId: restaurant?.id, locationName: restaurant?.name || 'Restaurant',
                            orderType: orderType === 'staff' ? 'staff' : 'standard',
                            writeoffNote: writeoffNote || null,
                            stockUsed: (stockUsed || []).map(u => ({ item: u.item, qty: u.qty })) }])
  }

  // switching the basket tier reprices everything already in it, EXCEPT
  // lines someone has hand-priced — those stay as entered
  function switchTier(t) {
    setDefaultTier(t)
    setBasket(b => b.map(l => (l.priceOverridden || !l.item) ? l
      : { ...l, tier: t, unitPrice: priceFor(l.item, t) }))
  }
  const patchLine = (key, patch) =>
    setBasket(b => b.map(l => l.key === key ? { ...l, ...patch } : l))
  const dropLine = (key) => setBasket(b => b.filter(l => l.key !== key))

  // Correct one of today's entries from "Match your Total Sales": a different
  // quantity and/or a different way of paying. Returns false if it failed.
  async function fixEntry(row, { qty, method }) {
    setSwitchingId(row.id)
    try {
      await updateSaleWithPayments(row.id, {
        qty, unitPrice: row.unit_price,
        payments: [{ method, amount: Number((qty * row.unit_price).toFixed(2)) }],
        businessDate: null })
      toast('Corrected', 'success')
      refresh()
      return true
    } catch (e) { toast('Not corrected: ' + e.message, 'error'); return false }
    finally { setSwitchingId(null) }
  }

  const [tillSaved, setTillSaved] = useState(false)
  // Whether THIS till has already been counted today, read from the
  // database rather than from tillSaved — which only remembers within
  // one session, so a reload put the big amber "Match your Total Sales
  // for the day" button back in front of someone who had already done
  // it (Chris, 24 October 2026).
  const [myTillDone, setMyTillDone] = useState(false)
  // Why the tile is covered when it is covered: a till nobody has
  // counted yet, or a check that failed. Kept apart on purpose.
  const [tillCheckError, setTillCheckError] = useState(null)
  // The day's detail, folded into the POS/Cash/Credit tile. Closed by
  // default: the three figures are what the page is opened for.
  const [detailOpen, setDetailOpen] = useState(false)
  // How many of the day's entries show before asking. Twenty was
  // hard-coded into the slice with nothing naming it.
  const ENTRY_PAGE = 20
  const [showAllToday, setShowAllToday] = useState(false)
  // Has the FRONT DESK closed this day? null while unknown.
  const [frontDeskClosed, setFrontDeskClosed] = useState(null)
  // Counted and settled, so it closes for the day (Chris, 2 November
  // 2026). `done` was passed as `myTillDone && !tillSaved`, which had it
  // backwards: saving flipped tillSaved to true, done went false, and the
  // big amber button came straight back to ask someone who had just
  // finished to count again.
  //
  // Only a CLEAN count closes it. A count that did not settle leaves the
  // prompt up, because that day is not finished — the figures still
  // disagree and somebody has to come back to them.
  const [tillMatched, setTillMatched] = useState(false)
  const saveTillResult = async (r) => {
    await recordTillCheck({
      branchId: staff.branch_id, locationId, date,
      appPos: r.appPos, appCash: r.appCash, pos: r.pos, cash: r.cash })
    setTillSaved(true)
    setTillMatched(!!r.allOk)
  }

  async function undoLast() {
    if (!window.confirm('Undo the last sale? It will be removed as if it never happened.')) return
    try {
      await undoRecentReceipt(lastReceiptId)
      toast('Last sale undone', 'success')
      setLastReceiptId(null); refresh()
    } catch (e) { toast('Not undone: ' + e.message, 'error') }
  }

  // finding 4: warn before recording more than the location holds
  function startPayment() {
    const over = basket.filter(l => l.item && l.qty > onHand(l.item.id, l.locationId))
    if (over.length) {
      const names = over.map(l => `${l.item.name} (${onHand(l.item.id, l.locationId)} left, selling ${l.qty})`).join('\n')
      if (!window.confirm(`More than the shelf shows:\n\n${names}\n\nRecord anyway?`)) return
    }
    // Rushing fat-finger check: a quantity far above what this item usually
    // sells in a day is more often a typo (12 instead of 2) than a sale.
    const big = basket.filter(l => {
      if (!l.item) return false
      const soldToday = Math.max(0, ...today.filter(r => r.stock_item_id === l.item.id)
        .map(r => Number(r.qty) || 0))
      return l.qty >= Math.max(10, 4 * soldToday)
    })
    if (big.length) {
      const names = big.map(l => `${l.item.name} × ${l.qty}`).join('\n')
      if (!window.confirm(`That is a big quantity:\n\n${names}\n\nIs it right?`)) return
    }
    setPaying({
      method: methods[0], split: null,
      customerId: null, newCustomer: '',
    })
  }

  const creditAmount = (p) =>
    p.split ? Number(p.split.credit || 0) : (p.method === 'credit' ? basketTotal : 0)

  // Occupied rooms only — a reservation has not arrived, so there is no
  // one at the bar to charge. Internal rooms (GM Office) excluded.
  useEffect(() => {
    if (!paying) return
    tracked('Guests in house', loadLiveStays(staff.branch_id))
      .then(rows => setInHouse((rows || [])
        .filter(r => r.status === 'occupied' && !r.rooms?.is_internal)
        .sort((a, b) => String(a.rooms?.room_number).localeCompare(
          String(b.rooms?.room_number), undefined, { numeric: true }))))
      .catch(() => setInHouse([]))
  }, [!!paying, staff.branch_id])

  const looksLikeRoom = (name) =>
    /\b(room|rm)\.?\s*\d+/i.test(name) || /^\s*\d{3}\s*$/.test(name)

  function chooseRoomBill(stay) {
    setPaying(p => ({ ...p, customerId: null, split: null, method: 'credit',
      roomStay: { id: stay.id, room: stay.rooms?.room_number, guestName: stay.guests?.full_name, since: stay.check_in_date } }))
    setGuestChoice(null); setRoomGuard(null)
  }

  async function chooseSeparateTab(stay) {
    try {
      const c = await getOrCreateGuestTab(staff.branch_id, stay.guests)
      setCustomers(cs => cs.some(x => x.id === c.id) ? cs : [...cs, c])
      setPaying(p => ({ ...p, customerId: c.id, roomStay: null }))
      setGuestChoice(null); setRoomGuard(null)
    } catch (e) { toast(e.message, 'error') }
  }

  async function createAndPick(name, servedBy) {
    try {
      const c = await createCustomer(staff.branch_id, name, servedBy)
      setCustomers(cs => cs.some(x => x.id === c.id) ? cs : [...cs, c])
      setPaying(p => ({ ...p, customerId: c.id }))
    } catch (e) { toast(e.message, 'error') }
  }

  // A basket saved twice in quick succession is the commonest way a sale is
  // double counted: the person taps Save, the phone is slow, they tap again.
  // The same items at the same price within two minutes, by the same person at
  // the same department, is therefore confirmed before it saves. A genuine
  // repeat (two customers, same beer) just says yes.
  function looksLikeRepeat() {
    const cutoff = Date.now() - 2 * 60 * 1000
    return basket.every(b => today.some(r =>
      r.recorded_by === staff.id
      && new Date(r.created_at).getTime() >= cutoff
      && (r.stock_item_id || null) === (b.item?.id || null)
      && Number(r.qty) === Number(b.qty)
      && Number(r.unit_price) === Number(b.unitPrice)))
  }

  // confirmed travels as an argument, not in state: a state update does
  // not resume the call it interrupted.
  async function commit(confirmed = false) {
    if (!canRecordHere) {
      toast('View only — you can record only at your own departments', 'error')
      return
    }
    // A backdated entry with no reason is the one nobody can explain a
    // week later, when the figure it moved is being argued about.
    if (needsReason && !backdateReason.trim()) {
      toast(`Say why this is going to ${dayShort(date)} — a reason is required beyond yesterday`, 'error')
      return
    }
    if (basket.length && looksLikeRepeat() &&
        !window.confirm('You recorded the same thing less than two minutes ago. '
          + 'Save it again?\n\nTap Cancel if your first save already went through.')) {
      return
    }
    // Room bill: the whole basket goes onto the guest's folio as a room
    // charge — not a sale, so not saveBasket. One order, one line per item.
    if (paying.roomStay) {
      setBusy(true)
      try {
        const res = await chargeBasketToRoom({ staff, stayId: paying.roomStay.id, lines: basket,
                                               locationId, date, locById })
        // Over the branch limit and made by bar staff: it waits for the
        // front desk. Say so plainly, so nobody thinks it is on the bill.
        // Kept as a branch, not deleted: with approvals off nothing is
        // ever pending, but if the limit is set again this message
        // returns on its own.
        if (res.status === 'pending') {
          toast(`Sent to front desk for approval · Room ${paying.roomStay.room} · ${naira(basketTotal)}. ` +
                'Not on the bill until they approve it.', 'success', { duration: 12000 })
        } else {
          toast(`Charged to Room ${paying.roomStay.room} · ${naira(basketTotal)}`, 'success')
        }
        setBasket([]); setPaying(null); setDefaultTier('general'); refresh()
      } catch (e) {
        toast(isConnectionError(e)
          ? 'No connection — room charges need to be online. Try again shortly.'
          : 'Not charged: ' + e.message, 'error')
      }
      setBusy(false)
      return
    }
    // The same thing, on the same account, a moment ago? Asked before
    // anything is written, and only for a named customer (see
    // checkRepeatSale).
    if (!confirmed && paying.customerId) {
      const prior = await checkRepeatSale({
        branchId: staff.branch_id, customerId: paying.customerId, lines: basket,
      })
      if (prior) { setRepeatSale(prior); return }
    }
    setBusy(true)
    try {
      const customerId = paying.customerId
      if (creditAmount(paying) > 0 && !customerId) {
        toast('Credit sales need a customer — pick or add one.', 'error'); setBusy(false); return
      }
      const payments = paying.split
        ? methods.map(m => ({ method: m, amount: Number(paying.split[m] || 0) }))
        : [{ method: paying.method, amount: basketTotal }]

      const allocated = payments.reduce((s, p) => s + p.amount, 0)
      const diff = Number((basketTotal - allocated).toFixed(2))
      if (Math.abs(diff) > 0.005) {
        const msg = diff > 0
          ? `${naira(diff)} unaccounted. Record it as credit, or correct the amount.`
          : `${naira(-diff)} more allocated than the sale is worth.`
        if (!canOverrideVariance) { toast(msg, 'error'); setBusy(false); return }
        if (!window.confirm(msg + '\n\nSave anyway? It will appear in the variance report.')) {
          setBusy(false); return
        }
      }

      const payload = {
        staffLite: { id: staff.id, branch_id: staff.branch_id },
        locationId, date, customerId,
        lines: basket.map(l => ({
          item: l.item ? { id: l.item.id, name: l.item.name } : null,
          description: l.description || null,
          tier: l.tier, qty: l.qty, unitPrice: l.unitPrice, locationId: l.locationId,
          orderType: l.orderType, writeoffNote: l.writeoffNote,
          stockUsed: (l.stockUsed || []).map(u => ({ item: { id: u.item.id, name: u.item.name }, qty: u.qty })) })),
        payments, backdateReason, onBehalfOf,
      }
      try {
        const rid = await saveBasket({ staff, locationId, lines: basket, payments,
                                       date, customerId, backdateReason, onBehalfOf })
        toast(`Saved · ${basket.length} item${basket.length > 1 ? 's' : ''} · ${naira(basketTotal)}`
              + (offToday ? ` · ${dayShort(date)}, not today` : ''), 'success')
        setLastReceiptId(rid); setLastReceiptAt(Date.now())
      } catch (e) {
        if (!isConnectionError(e)) throw e
        enqueue({ kind: 'basket', payload })
        toast('No connection — saved and will send when you are back online')
      }
      setBasket([]); setPaying(null); setDefaultTier('general'); refresh(); flush()
    } catch (e) {
      if (e.partial) { setBasket([]); setPaying(null); refresh() }
      toast((e.partial ? '' : 'Not saved: ') + e.message, 'error')
    }
    setBusy(false)
  }

  async function commitWriteoff() {
    if (!canRecordHere) {
      toast('View only — you can record only at your own departments', 'error')
      return
    }
    setBusy(true)
    const w = writeoff
    try {
      const wDate = w.date || date
      const payload = { staffLite: { id: staff.id, branch_id: staff.branch_id },
        itemId: w.item.id, locationId, kind: w.kind, qty: w.qty, unitValue: w.unitValue, date: wDate,
        note: w.note || null, damageReason: w.kind === 'damage' ? (w.damageReason || null) : null }
      try {
        await saveWriteoff({ staff, item: w.item, locationId, kind: w.kind,
          qty: w.qty, unitValue: w.unitValue, date: wDate,
          note: w.note || null, damageReason: w.kind === 'damage' ? (w.damageReason || null) : null })
        toast(`${w.qty} × ${w.item.name} recorded as ${w.kind === 'damage' ? 'damaged' : 'PR'}`
              + (wDate !== todayDate ? ` · ${dayShort(wDate)}, not today` : ''), 'success')
      } catch (e) {
        if (!isConnectionError(e)) throw e
        enqueue({ kind: 'writeoff', payload })
        toast('No connection — saved and will send when you are back online')
      }
      setWriteoff(null); refresh(); flush()
    } catch (e) { toast('Not saved: ' + e.message, 'error') }
    setBusy(false)
  }

  const todayTotal = today.reduce((s, r) => s + Number(r.amount || r.qty * r.unit_price), 0)

  return (
    <div className="px-5 pb-40">
      <div className="flex gap-2 overflow-x-auto py-2 -mx-1 px-1">
        {salesPoints.map(l => (
          <button key={l.id} onClick={() => setLocationId(l.id)}
            className={`shrink-0 h-11 px-4 rounded-full border ${l.id === locationId
              ? 'bg-amber text-bg border-amber font-bold' : 'border-line text-dim'}`}>
            {l.name}
          </button>
        ))}
      </div>

      {/* Pinned, so it is still on screen at the tenth entry. Nothing
          at all on an ordinary day — the normal path stays clean.

          Two tones. Closing yesterday only needs the day stated, and an
          alarm shown every morning stops being read by the second week.
          Older than that keeps the amber. */}
      {offToday && (
        <div className={`sticky top-0 z-20 -mx-5 px-5 py-2 font-bold text-sm text-center ${
          needsReason ? 'bg-amber text-bg' : 'bg-raise text-amber border-b border-amber/40'}`}>
          Recording for {new Date(date + 'T12:00:00').toLocaleDateString('en-NG',
            { weekday: 'long', day: 'numeric', month: 'long' })}
          {needsReason ? ' — not today' : ' — yesterday'}
        </div>
      )}

      {canBackdate ? (
        <div className="mt-2">
          <div className="flex items-center gap-3">
          <input type="date" value={date} max={todayDate} min={earliestDate}
            onChange={e => {
              const v = e.target.value
              if (!v) return
              if (v > todayDate) {
                setDate(todayDate); toast('You cannot post a future date', 'error'); return
              }
              if (earliestDate && v < earliestDate) {
                setDate(earliestDate)
                toast(canOverrideVariance
                  ? 'That is before the opening balance'
                  : `You can only post back ${STAFF_BACKDATE_DAYS} days`, 'error')
                return
              }
              setDate(v)
            }}
            className={`h-12 px-3 rounded-xl bg-surface border tnum ${needsReason
              ? 'border-amber text-amber' : offToday ? 'border-amber/40' : 'border-line'}`} />
          </div>
          {/* The reason is asked once, in the payment sheet, at the
              moment the sale is saved (Chris, 19 October 2026). It was
              here as well, which put six chips between the date and
              Sell Item on every backdated entry and asked the same
              question twice. */}
        </div>
      ) : null}
      {/* The pinned amber band above already says which day this is,
          and keeps saying it. A paragraph repeating it put the same
          sentence on screen three times — band, paragraph, button —
          and pushed Sell Item further down the page. The backdate
          limit is said once, where it bites: when the day is not today
          (18 October 2026). */}
      {canBackdate && !canOverrideVariance && offToday && (
        <p className="mt-1 text-dim text-sm">
          You can post up to {STAFF_BACKDATE_DAYS} days back. Ask a manager for anything older.
        </p>
      )}

      {/* Price tiers and PR/Damage only matter when recording. */}
      {!isReception && !isRestaurant && tiers.length > 1 && canRecordHere && (
        <div className="mt-3">
          <div className="flex gap-2">
            {tiers.map(t => (
              <button key={t} onClick={() => switchTier(t)}
                className={`flex-1 h-12 rounded-xl border font-bold ${defaultTier === t
                  ? 'bg-amber text-bg border-amber' : 'border-line text-dim'}`}>
                {tierLabel[t] || t}
              </button>
            ))}
            {/* Bar and minimart staff record their own PR and breakages
                (migration 283): making them find a manager meant it went
                unrecorded and surfaced later as an unexplained shortage.
                Every line shows on the daily close with who recorded it.
                Auditors are excluded — they review write-offs. */}
            {canWriteOff && (
              <button onClick={() => { setWriteoffMode(true); setPicking(true) }}
                className="flex-1 h-12 rounded-xl border border-clay text-clay font-bold">
                PR / Damage
              </button>
            )}
          </div>
          {defaultTier !== 'general' && (
            <p className="mt-2 text-amber text-sm">
              {tierLabel[defaultTier]} prices — everything added is priced at this tier.
              {defaultTier === 'staff' && ' Name the staff member below so the receipt shows who took it.'}
            </p>
          )}
        </div>
      )}

      {(!isReception || canRecordHere) && canChargeToRoom && (
        <button onClick={() => setRoomCharging(true)}
          className="mt-3 w-full h-12 rounded-xl border border-line text-ink font-semibold">
          Charge to a room
        </button>
      )}

      {/* Typed orders belong to the Restaurant alone: its food is cooked
          to order and has no catalog price to tap. MainBar, Minimart and
          OpenBar sell from the catalog, so a free-text order there is a
          way to sell something off-book at any price — the button was
          showing in all three and is now gone. Reception types restaurant
          orders for a guest, but it does that inside Charge to a room. */}
      {isRestaurant && canRecordHere && (
        <button onClick={() => setRestaurantOrder({ description: '', qty: 1, unitPrice: '',
          orderType: 'standard', damageReason: null, writeoffNote: '', prMeal: null, date: null, used: [], noStock: false })}
          className="mt-3 w-full h-12 rounded-xl border border-line text-ink font-semibold">
          Add a restaurant order
        </button>
      )}

      {!isReception && canRecordOnBehalf && people.length > 0 && (
        <div className="mt-3">
          <div className="text-dim text-sm mb-2">Recording on behalf of (staff at this location)</div>
          <select value={onBehalfOf || ''} onChange={e => setOnBehalfOf(e.target.value || null)}
            className="h-12 w-full px-3 rounded-xl bg-surface border border-line">
            <option value="">Myself</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
          {onBehalfOf && (
            <p className="mt-1 text-amber text-sm">
              This sale will appear on {people.find(p => p.id === onBehalfOf)?.full_name}'s records.
            </p>
          )}
        </div>
      )}

      {isReception ? (
        // Reception doesn't sell catalog stock at all — check-in,
        // checkout, and room charges belong on the Rooms tab. Shows a
        // real activity feed below (payments collected today), since
        // Reception has no sales rows at all by design — the parallel
        // to a bar's daily sales here is money collected, not items sold.
        <div className="mt-3 rounded-2xl border border-line bg-surface p-4">
          <p className="font-semibold">Reception doesn't record sales here.</p>
          <p className="text-dim text-sm mt-1">
            Check-in, checkout, room status, and settling a guest's bill are all
            on the Rooms tab.
          </p>
        </div>
      ) : !isRestaurant && canRecordHere && (
        <button onClick={() => setPicking(true)}
          className="mt-3 w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold active:bg-amber-deep">
          + Sell Item
        </button>
      )}

      {approvalsOn && !isReception && canRecordHere && (
        <MyPendingRoomCharges branchId={staff.branch_id} staffId={staff.id} onDecided={refresh} />
      )}

      {approvalsOn && !isReception && canRecordHere && (
        <CollectRefusedCharges branchId={staff.branch_id} locationId={locationId} canOverride={isGmOrAdmin}
          methods={methods} customers={customers}
          onCreateCustomer={async (name, servedBy) => {
            try {
              const c = await createCustomer(staff.branch_id, name, servedBy)
              setCustomers(cs => cs.some(x => x.id === c.id) ? cs : [...cs, c])
              return c
            } catch (e) { toast(e.message, 'error'); return null }
          }}
          onCollected={refresh} />
      )}

      {/* Shown instead of the recording buttons, so the missing
          controls read as deliberate rather than as a broken page. */}
      {!canRecordHere && (
        <Notice tone="note" className="mt-3">
          Viewing {currentDept?.name || 'this department'}. You can see its sales here,
          but you can only record sales at your own departments.
        </Notice>
      )}

      <HandoverNotes branchId={staff.branch_id} locationId={locationId} />

      {!isReception && (
        <RoomChargeTracker branchId={staff.branch_id} locationId={locationId} refreshKey={lastReceiptAt} />
      )}

      {approvalsOn && isReception && canApprove && (
        <RoomChargeApprovals branchId={staff.branch_id} onDecided={refresh} />
      )}

      {isReception && receptionDashboard && (
        <>
          {/* The front desk's dashboard is this tile's detail, the same
              way a department's day is. Closed, Reception also reads as
              what it should have in hand and nothing else. */}
          <p className="text-dim text-sm mt-6">Closing the day</p>
          <TillTile figures={tileFigures} note={tileNote} hidden={isMyTill && !tillSaved && !myTillDone}
                    checkError={tillCheckError} open={detailOpen}
                    onToggle={() => setDetailOpen(o => !o)}>
            <ReceptionDashboard dashboard={receptionDashboard} headless className=""
              canSeeInternal={isGmOrAdmin} roomsSold={roomsSold} showRoomsSold={isGmOrAdmin} />
          </TillTile>
          <MatchTill rows={[]} posTotal={inHand.pos} cashTotal={inHand.cash}
            includes={{ handedOutPos: handedOut.pos, handedOutCash: handedOut.cash }}
            done={myTillDone || tillMatched} onRecord={saveTillResult} />
        </>
      )}

      {isReception && (
        <div className="mt-3">
          <button onClick={() => setShowYesterday(x => !x)}
            className="w-full h-11 rounded-xl border border-line text-dim font-semibold text-sm">
            {showYesterday ? 'Hide' : 'Show'} yesterday's snapshot ({lagosDaysAgo(1)})
          </button>
          {showYesterday && (
            <div className="mt-2 rounded-2xl border border-line bg-surface p-4">
              {yesterdaySummary ? (
                <div className="grid grid-cols-3 gap-3 pb-3 mb-3 border-b border-line">
                  <div>
                    <div className="text-dim text-sm">POS</div>
                    <div className="tnum font-bold">{naira((yesterdaySummary.byMethod || {}).pos || 0)}</div>
                  </div>
                  <div>
                    <div className="text-dim text-sm">Cash</div>
                    <div className="tnum font-bold">{naira((yesterdaySummary.byMethod || {}).cash || 0)}</div>
                  </div>
                  <div>
                    <div className="text-dim text-sm">Credit raised</div>
                    <div className="tnum font-bold text-clay">{naira(yesterdaySummary.creditRaised)}</div>
                  </div>
                </div>
              ) : <p className="text-dim text-sm">Loading…</p>}

              <p className="text-dim text-sm mb-1">Payments collected</p>
              {(yesterdayActivity || []).map(p => (
                <div key={p.id} className="flex justify-between text-sm py-1 border-b border-line/60 last:border-0">
                  <span className="truncate">
                    {p.stays?.guests?.full_name || 'Guest'} · Room {p.stays?.rooms?.room_number || '—'}
                    <span className="text-dim"> · {methodLabel[p.method] || p.method}</span>
                  </span>
                  <span className="tnum font-semibold">{naira(p.amount)}</span>
                </div>
              ))}
              {yesterdayActivity && !yesterdayActivity.length && (
                <p className="text-dim text-sm">No payments that day.</p>
              )}
              {!yesterdayActivity && <p className="text-dim text-sm">Loading…</p>}
            </div>
          )}
        </div>
      )}

      {isReception && (
        <div className="mt-4">
          <div className="rounded-2xl border border-line bg-surface p-4">
            <div className="text-dim text-sm">
              Front desk · collected{' '}
              {offToday
                ? new Date(date + 'T12:00:00').toLocaleDateString('en-NG',
                    { weekday: 'long', day: 'numeric', month: 'long' })
                : 'today'}
            </div>
            <div className="tnum text-2xl font-bold">
              {naira(receptionActivity.reduce((s, p) => s + Number(p.amount || 0), 0))}
            </div>
            <div className="text-dim text-sm mt-1">
              {receptionActivity.length} {receptionActivity.length === 1 ? 'payment' : 'payments'}
              {' '}from guests
            </div>
          </div>
          <ul className="mt-2 divide-y divide-line/60">
            {receptionActivity.map(p => (
              <li key={p.id} className="py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">
                    {p.stays?.guests?.full_name || 'Guest'} · Room {p.stays?.rooms?.room_number || '—'}
                  </div>
                  <div className="text-dim text-sm">
                    {methodLabel[p.method] || p.method}{p.is_overstay ? ' · over-stay' : ''}
                    {p.staff?.full_name && ` · collected by ${p.staff.full_name}`}
                    {p.remark ? ` · ${p.remark}` : ''}
                  </div>
                </div>
                <span className="tnum font-semibold">{naira(p.amount)}</span>
              </li>
            ))}
            {!receptionActivity.length && (
              <li className="py-6 text-dim">No payments recorded yet today.</li>
            )}
          </ul>
        </div>
      )}

      {!!basket.length && (
        <ul className="mt-4 divide-y divide-line/60 rounded-2xl border border-line bg-surface px-4">
          {basket.map(l => (
            <li key={l.key} className="py-3">
              <div className="flex items-center gap-3">
                <button onClick={() => setTuning(l.key)} className="flex-1 min-w-0 text-left">
                  <div className="font-semibold truncate">{l.item?.name || l.description}</div>
                  <div className="text-sm">
                    <span className={l.tier === 'general' ? 'text-dim' : 'text-amber font-semibold'}>
                      {tierLabel[l.tier] || l.tier}
                    </span>
                    <span className="text-dim"> · {naira(l.unitPrice)} each</span>
                    {l.locationName && <span className="text-dim"> · from {l.locationName}</span>}
                  </div>
                </button>
                <button onClick={() => patchLine(l.key, { qty: Math.max(1, l.qty - 1) })}
                  className="h-11 w-11 rounded-xl bg-raise border border-line text-2xl">−</button>
                <span className="tnum w-8 text-center font-bold">{l.qty}</span>
                <button onClick={() => patchLine(l.key, { qty: l.qty + 1 })}
                  className="h-11 w-11 rounded-xl bg-raise border border-line text-2xl">+</button>
                <span className="tnum w-20 text-right">{naira(l.qty * l.unitPrice)}</span>
              </div>
              {!!l.stockUsed?.length && (
                <p className="text-dim text-sm mt-1">
                  Stock used: {l.stockUsed.map(u => `${u.qty} × ${u.item.name}`).join(', ')}
                </p>
              )}
              {l.item && l.qty > onHand(l.item.id, l.locationId) && (
                <p className="text-clay text-sm mt-1">Only {l.item && onHand(l.item.id, l.locationId)} on the shelf</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {lastReceiptId && (
        <button onClick={() => openReceipt(lastReceiptId)}
          className="mt-3 w-full h-12 rounded-xl border border-amber text-amber font-semibold">
          Receipt for the last sale
        </button>
      )}

      {lastReceiptId && Date.now() - lastReceiptAt < 10 * 60 * 1000 && lastReceiptAt > 0 && (
        <button onClick={undoLast}
          className="mt-2 w-full h-11 rounded-xl border border-line text-dim font-semibold text-sm">
          Wrong? Undo the last sale (10 minutes)
        </button>
      )}

      {!isReception && (
      <section className="mt-6">
        {/* "Today" over a date, with a figure on the right that named
            neither the department nor what it counted. It counts sales
            rung up at THIS till — not room charges, which are goods
            gone out on a guest's bill and are named separately, on
            their own line, so the two can never be added by eye. */}
        <div className="rounded-2xl border border-line bg-surface p-4">
          <div className="text-dim text-sm">
            {currentDept?.name || 'This department'} · sold here{' '}
            {offToday
              ? new Date(date + 'T12:00:00').toLocaleDateString('en-NG',
                  { weekday: 'long', day: 'numeric', month: 'long' })
              : 'today'}
          </div>
          <div className="tnum text-2xl font-bold">{naira(todayTotal)}</div>
          <div className="text-dim text-sm mt-1">
            {combinedToday.length} {combinedToday.length === 1 ? 'entry' : 'entries'}
            {roomChargedToday > 0 && (
              <> · {naira(roomChargedToday)} more charged to guests&#39; rooms</>
            )}
          </div>
        </div>

        {/* Counting is a separate job from selling, and it happens once,
            at the end. It is named here so the controls for it read as
            one piece of work rather than as more of the page.

            The tile goes ABOVE the count form, which is also what its
            own words say: "type your POS slip and your drawer into
            Match your Total Sales below". It sat underneath that form
            and still said "below". Nothing leaks by moving it — the
            tile stays covered until the count is saved, which is the
            whole reason it can stand here at all (18 October 2026). */}
        <p className="text-dim text-sm mt-6">Closing the day</p>

        {!isReception && frontDeskClosed === false && (
          <Notice tone="warn" title="The front desk has not closed this day yet" className="mt-3">
            When Reception records a guest paying for something you charged to their
            room, it lands on this day — so this figure can still rise after you have
            counted. Count once they have closed, or expect it to move.
          </Notice>
        )}

        {summary && (
        <TillTile figures={tileFigures} note={tileNote} hidden={isMyTill && !tillSaved && !myTillDone}
                  checkError={tillCheckError} open={detailOpen}
                  onToggle={() => setDetailOpen(o => !o)}>
        {recon && (
          <div>
            <div className="text-dim text-sm">Gross sales — value of goods sold</div>
            <div className="tnum text-3xl font-bold text-amber">{naira(recon.grossSales)}</div>
            <div className="mt-3 space-y-1 text-sm">
              {recon.roomNights && recon.roomNights.total > 0.009 && (
                <Line label={`Room nights — ${recon.roomNights.rows?.length || 0} slept and paid for`}
                      value={recon.roomNights.total} />
              )}
              <Line label="Received at sale (POS + Cash)" value={recon.received} />
              <Line label={recon.unqualifiedCredit > 0 ? 'Credit raised — counted as sales' : 'Credit raised'}
                value={recon.creditRaised}
                tone={recon.creditRaised > 0 ? 'text-clay' : ''} />
              {recon.unqualifiedCredit > 0 && (
                <div className="flex justify-between gap-3 text-clay">
                  <span className="min-w-0">Not repaid by noon next day — excluded from sales above</span>
                  <span className="tnum shrink-0">{naira(recon.unqualifiedCredit)}</span>
                </div>
              )}
              {/* ONE heading for every debt that came back today, because
                  that is what both of these are. A guest who takes a drink
                  to their room has been given credit exactly as a customer
                  has — the only difference is who collects it. Chris's
                  call, 17 October 2026: a room charge reads as credit to
                  the guest, and the money as debt recovered on the day the
                  guest pays. The arithmetic is unchanged; what changed is
                  that the staff now meet one idea instead of two. */}
              <Line label="Debt recovered" value={recon.debtRecovered} tone="text-leaf" />
              {recon.recoveredFromCustomers > 0 && (
                <>
                  <SubLine label="· from customer accounts" value={recon.recoveredFromCustomers} />
                  <ByMethod map={recon.customerRecoveredBy} labels={methodLabel} />
                </>
              )}
              {/* ONE line for room-bill money, read from the charge ticks.
                  There used to be two, three rows apart, contradicting
                  each other: "paid HERE" from the old till tag, and "the
                  FRONT DESK collected this" from the ticks. On 16
                  October both read ₦4,800 for MainBar, which has no
                  terminal, so only one could be true — and only the tag
                  one reached the total, which is why every day since the
                  tags were cleared showed none of this money at all.
                  Restaurant's 15 October was short by ₦98,500. */}
              {recon.recoveredFromRooms > 0 && (
                <>
                  <SubLine label="· from guests' room bills" value={recon.recoveredFromRooms} />
                  <ByMethod map={recon.roomRecoveredBy} labels={methodLabel} />
                  <p className="text-dim text-xs pl-4">
                    Guests paying off what you charged to their rooms, collected by the front
                    desk — POS money is on their slip, cash is handed over at close. Not part
                    of gross sales above.
                  </p>
                </>
              )}
              <div className="pt-2 mt-2 border-t border-line flex justify-between gap-3 font-bold">
                <span className="min-w-0">Total income for the day</span>
                <span className="tnum shrink-0">{naira(recon.totalMoneyIn)}</span>
              </div>

              {/* Below the total on purpose: goods that have left the
                  department but nobody has paid for yet. Naming what is
                  still to come is what stops the card looking short. */}
              {roomChargedToday > 0 && (
                <div className="pt-2 mt-2 border-t border-line/60">
                  <Line label="Credit given to guests' rooms today" value={roomChargedToday} tone="text-dim" />
                  <p className="text-dim text-xs pl-4">
                    Goods that left this department on a guest&#39;s room bill. Credit given to
                    the guest, not money, so not in the total above — it comes back to you as
                    debt recovered on the day they pay at Reception.
                  </p>
                </div>
              )}
            </div>

            {/* Below the day's total and outside its border: neither PR
                nor damage is money, and neither is part of what the
                cashier hands over.

                PR and damage are shown SEPARATELY and itemised. They are
                different things — PR is a decision someone made, damage
                is a loss — and "Damaged 6 units · ₦22,000" tells you
                neither what broke nor why. Each line names the item,
                quantity, cost, reason and who recorded it, so it can be
                checked against the breakages. */}
            <WriteoffBreakdown data={writeoffs} settled={recon} />

          </div>
        )}
        </TillTile>
        )}

        <MatchTill rows={today} busyId={switchingId} onFix={fixEntry} onRecord={saveTillResult}
          done={myTillDone || tillMatched}
          posTotal={inHand.pos} cashTotal={inHand.cash}
          includes={{ recoveredPos: recon?.customerRecoveredBy?.pos, recoveredCash: recon?.customerRecoveredBy?.cash,
                      roomPos: recon?.roomRecoveredBy?.pos, roomCash: recon?.roomRecoveredBy?.cash }}
          // Credit comes from the person's own entries (what they actually gave),
          // NOT the day summary's "credit raised", which leaves out credit not
          // repaid by noon next day and so showed 0 for same-day credit.
          creditApp={today.length ? null : inHand.credit}
          itemName={r => itemById[r.stock_item_id]?.name || r.description || ''} />

        {/* Every entry, not the newest twenty with no sign there were
            more. On a busy night the morning's sales fell off the
            bottom of the list and nothing said so, so a staff member
            checking one could not find it and had no way to know the
            list had been cut. */}
        <div className="flex items-baseline justify-between mt-6">
          <p className="text-dim text-sm">
            {combinedToday.length} {combinedToday.length === 1 ? 'entry' : 'entries'}
            {!showAllToday && combinedToday.length > ENTRY_PAGE && (
              <span> · newest {ENTRY_PAGE}</span>
            )}
          </p>
          {combinedToday.length > ENTRY_PAGE && (
            <button onClick={() => setShowAllToday(x => !x)}
              className="text-amber text-sm font-semibold shrink-0 py-2.5 pl-3">
              {showAllToday ? `Show newest ${ENTRY_PAGE}` : `Show all ${combinedToday.length}`}
            </button>
          )}
        </div>

        <ul className="mt-1 divide-y divide-line/60">
          {(showAllToday ? combinedToday : combinedToday.slice(0, ENTRY_PAGE)).map(r => (
            <li key={`${r.entryKind}:${r.id}`} className="py-3 flex items-center gap-3"
                onClick={() => r.entryKind === 'sale' && openReceipt(r.receipt_id)}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  <span className="truncate">{itemById[r.stock_item_id]?.name || r.description || '—'}</span>
                  {r.order_type === 'pr_damage' && (
                    <span className="shrink-0 text-xs font-bold text-clay border border-clay rounded-full px-2 py-0.5">
                      PR / Damage
                    </span>
                  )}
                  {r.order_type === 'staff' && (
                    <span className="shrink-0 text-xs font-bold text-amber border border-amber rounded-full px-2 py-0.5">
                      Staff
                    </span>
                  )}
                </div>
                {r.entryKind === 'roomCharge' ? (
                  <div className="text-dim text-sm">
                    {r.qty} × {naira(r.unit_price)}
                    <br />Charged to Room {r.roomNumber || '—'}
                    {r.guestName ? ` · ${r.guestName}` : ''}
                    {r.order_type === 'pr_damage' && ' · not paid for'}
                    {(r.damage_reason || r.pr_meal) && ` · ${r.damage_reason || r.pr_meal}`}
                    {r.writeoff_note && ` · ${r.writeoff_note}`}
                    {r.no_stock_declared && <span className="text-clay"> · no stock declared</span>}
                    <br />Charged by {r.chargedBy || 'unknown'}
                    {r.sortAt ? ` · ${new Date(r.sortAt).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })}` : ''}
                    {r.lineStatus && (
                      <>
                        {' · '}
                        {/* The stay's position, not a claim about this line.
                            Payments attach to the stay, so which line any
                            payment settled is not recorded (migration 440). */}
                        {{ settled: <span className="text-leaf">guest has paid</span>,
                           running: <span className="text-amber">on the running bill</span>,
                           debt: <span className="text-clay font-bold">unpaid — guest has checked out</span>,
                        }[r.lineStatus]}
                      </>
                    )}
                  </div>
                ) : r.order_type === 'pr_damage' ? (
                  <div className="text-dim text-sm">
                    {r.qty} × {naira(r.unit_price)} · not paid for
                    {(r.damage_reason || r.pr_meal) && ` · ${r.damage_reason || r.pr_meal}`}
                    {r.writeoff_note && ` · ${r.writeoff_note}`}
                    {/* The tick that let it save without stock (migration 434),
                        shown on the line itself so it is seen without opening
                        the day's close. */}
                    {r.no_stock_declared && <span className="text-clay"> · no stock declared</span>}
                    <br />{whoRecorded(r)}
                  </div>
                ) : r.order_type === 'staff' ? (
                  <div className="text-dim text-sm">
                    {tierLabel[r.tier] || r.tier} · {r.qty} × {naira(r.unit_price)}
                    {r.writeoff_note && ` · ${r.writeoff_note}`}
                    <br />{paymentSummary(r)} · {whoRecorded(r)}
                  </div>
                ) : (
                  <div className="text-dim text-sm">
                    {tierLabel[r.tier] || r.tier} · {r.qty} × {naira(r.unit_price)}
                    {r.business_date !== r.created_at?.slice(0, 10) && (
                      <span className="ml-2 text-amber">backdated</span>
                    )}
                    <br />{paymentSummary(r)} · {whoRecorded(r)}
                  </div>
                )}
                {!!r.order_stock_used?.length && (
                  <div className="text-dim text-sm">
                    Stock used: {r.order_stock_used.map(u => `${Number(u.qty)} × ${u.stock_items?.name || 'item'}`).join(', ')}
                  </div>
                )}
              </div>
              <div className="tnum font-semibold">{naira(r.amount ?? r.qty * r.unit_price)}</div>
              {canDeleteTraining && (
                <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(r) }}
                  className="h-9 px-3 rounded-lg border border-clay text-clay text-sm font-semibold shrink-0">
                  Delete
                </button>
              )}
            </li>
          ))}
          {!combinedToday.length && <li className="py-6 text-dim">No sales recorded yet — the first one goes on top.</li>}
        </ul>
      </section>
      )}

      {!!basket.length && (
        <div className="fixed bottom-20 inset-x-0 px-5 pb-2 z-20">
          <button onClick={startPayment}
            className="w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold shadow-lg active:bg-amber-deep">
            Take payment · {naira(basketTotal)}
          </button>
        </div>
      )}

      {picking && (
        <ItemPicker items={items} stockMap={stockMap} locationId={locationId}
          popular={popular}
          onPick={item => {
            if (writeoffMode) {
              setPicking(false); setWriteoffMode(false)
              setWriteoff({ item, kind: 'damage', qty: 1, unitValue: Number(item.selling_price) })
            } else addToBasket(item)
          }}
          onClose={() => { setPicking(false); setWriteoffMode(false) }} />
      )}

      {tuning && (() => {
        const l = basket.find(x => x.key === tuning)
        if (!l) return null
        return (
          <Sheet onClose={() => setTuning(null)}>
            <h2 className="text-2xl font-bold">{l.item?.name || l.description}</h2>
            {l.item ? (
              <>
                <Row label="Price tier">
                  {tiers.map(t => (
                    <Chip key={t} active={l.tier === t}
                      onClick={() => patchLine(l.key, { tier: t, unitPrice: priceFor(l.item, t), priceOverridden: false })}>
                      {tierLabel[t] || t}
                    </Chip>
                  ))}
                </Row>
                <Row label="Unit price">
                  <input type="number" inputMode="decimal" value={l.unitPrice}
                    onChange={e => patchLine(l.key, { unitPrice: Number(e.target.value), priceOverridden: true })}
                    className="h-12 w-36 px-3 rounded-xl bg-surface border border-line tnum" />
                  {l.priceOverridden && (
                    <button onClick={() => patchLine(l.key, { unitPrice: priceFor(l.item, l.tier), priceOverridden: false })}
                      className="text-dim text-sm underline">Reset to {tierLabel[l.tier] || l.tier} price</button>
                  )}
                </Row>
              </>
            ) : (
              // typed order — no catalog item, so no tier concept;
              // just the description and the price as entered
              <>
                <Row label="Order">
                  <input value={l.description}
                    onChange={e => patchLine(l.key, { description: e.target.value })}
                    className="h-12 w-full px-3 rounded-xl bg-surface border border-line" />
                </Row>
                <Row label="Unit price">
                  <input type="number" inputMode="decimal" value={l.unitPrice}
                    onChange={e => patchLine(l.key, { unitPrice: Number(e.target.value) })}
                    className="h-12 w-36 px-3 rounded-xl bg-surface border border-line tnum" />
                </Row>
              </>
            )}
            <button onClick={() => { dropLine(l.key); setTuning(null) }}
              className="mt-8 w-full h-12 rounded-xl border border-clay text-clay font-semibold">
              Remove from basket
            </button>
            <button onClick={() => setTuning(null)}
              className="mt-3 w-full h-14 rounded-2xl bg-amber text-bg text-lg font-bold">Done</button>
          </Sheet>
        )
      })()}

      {paying && (
        <Sheet onClose={() => setPaying(null)}>
          <h2 className="text-2xl font-bold">Payment</h2>
          <p className="mt-1 text-3xl tnum text-amber font-bold">{naira(basketTotal)}</p>
          <p className="text-dim mt-1">{basket.length} item{basket.length > 1 ? 's' : ''}</p>

          <Row label="Paid by">
            {methods.map(m => (
              <Chip key={m} active={!paying.split && paying.method === m}
                onClick={() => setPaying(p => ({ ...p, method: m, split: null }))}>
                {methodLabel[m] || m}
              </Chip>
            ))}
            <Chip active={!!paying.split}
              onClick={() => setPaying(p => ({ ...p,
                split: p.split || Object.fromEntries(methods.map(m => [m, ''])) }))}>
              Split
            </Chip>
          </Row>

          <Allocation total={basketTotal} paying={paying} methods={methods}
            onCredit={() => setPaying(p => {
              const alloc = p.split
                ? Object.entries(p.split).reduce((s, [k, v]) => k === 'credit' ? s : s + Number(v || 0), 0)
                : (p.method === 'credit' ? 0 : basketTotal)
              const split = p.split || Object.fromEntries(methods.map(m =>
                [m, m === p.method ? String(basketTotal) : '']))
              return { ...p, split: { ...split, credit: String(Math.max(basketTotal - alloc, 0)) } }
            })} />

          {paying.split && (
            <div className="mt-3 space-y-2">
              {methods.map(m => (
                <div key={m} className="flex items-center gap-3">
                  <span className="w-20 text-dim">{methodLabel[m] || m}</span>
                  <input type="number" inputMode="decimal" placeholder="0" value={paying.split[m]}
                    onChange={e => setPaying(p => ({ ...p, split: { ...p.split, [m]: e.target.value } }))}
                    className="h-12 flex-1 px-3 rounded-xl bg-surface border border-line tnum" />
                </div>
              ))}
              <SplitCheck split={paying.split} total={basketTotal} />
            </div>
          )}

          {paying.roomStay ? (
            <div className="mt-6 rounded-2xl border border-amber bg-surface p-4">
              <div className="text-dim text-sm">Room bill</div>
              <div className="font-bold text-lg">
                Room {paying.roomStay.room} · {paying.roomStay.guestName}
              </div>
              {paying.roomStay.since && (
                <div className="text-dim text-sm">Checked in {niceDay(paying.roomStay.since)}</div>
              )}
              <p className="text-dim text-sm mt-1">
                The whole sale ({naira(basketTotal)}) goes on this guest's room bill
                and is collected at checkout.
              </p>
              <button onClick={() => setPaying(p => ({ ...p, roomStay: null }))}
                className="mt-2 text-amber text-sm underline">Change</button>
            </div>
          ) : (
          <div className="mt-6">
            {/* Guests first, at the moment the credit decision is made —
                the whole point. Previously a bartender reached this step
                already committed to a customer account, and the correct
                path (charge to room) was a separate button further up. */}
            {creditAmount(paying) > 0 && !!inHouse.length && !guestChoice && !roomGuard && (
              <div className="mb-5">
                <div className="text-dim mb-2">In-house guest?</div>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {inHouse.map(st => (
                    <button key={st.id} onClick={() => { setRoomGuard(null); setGuestChoice(st) }}
                      className="w-full h-12 px-4 rounded-xl border border-line text-left flex items-center gap-3">
                      <span className="font-bold tnum w-20 shrink-0">Rm {st.rooms?.room_number}</span>
                      <span className="truncate">{st.guests?.full_name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {guestChoice && (
              <div className="mb-5 rounded-2xl border border-amber bg-surface p-4">
                <div className="font-bold">
                  Room {guestChoice.rooms?.room_number} · {guestChoice.guests?.full_name}
                </div>
                {canChargeToRoom ? (<>
                  <p className="mt-2 text-sm font-semibold">
                    Charge {naira(basketTotal)} to Room {guestChoice.rooms?.room_number} · {guestChoice.guests?.full_name}
                    {guestChoice.check_in_date ? ` (checked in ${niceDay(guestChoice.check_in_date)})` : ''}
                  </p>
                  <p className="text-dim text-sm mt-1">
                    Is this the right guest? Check the name and room before charging.
                  </p>
                  <button onClick={() => chooseRoomBill(guestChoice)}
                    className="mt-3 w-full h-12 rounded-xl bg-amber text-bg font-bold">
                    Add to room bill
                  </button>
                  <button onClick={() => chooseSeparateTab(guestChoice)}
                    className="mt-2 w-full h-12 rounded-xl border border-amber text-amber font-semibold">
                    Separate bar tab
                  </button>
                  <p className="text-dim text-xs mt-2">
                    A separate tab is billed on its own (e.g. for company expenses) but
                    still shows to the front desk at checkout.
                  </p>
                </>) : (<>
                  {/* The room bill is no longer an option here. The guest
                      is still worth naming — a tab in their name is this
                      department's own debt, collected by this department,
                      and the front desk can still see it at checkout. */}
                  <p className="mt-2 text-sm font-semibold">
                    Put {naira(basketTotal)} on this guest's own tab
                  </p>
                  <p className="text-dim text-sm mt-1">
                    This department collects its own sales. The tab is yours to collect —
                    it does not go on the room bill.
                  </p>
                  <button onClick={() => chooseSeparateTab(guestChoice)}
                    className="mt-3 w-full h-12 rounded-xl bg-amber text-bg font-bold">
                    Open the tab
                  </button>
                </>)}
                <button onClick={() => setGuestChoice(null)}
                  className="mt-2 w-full h-10 text-dim text-sm">Cancel</button>
              </div>
            )}

            {roomGuard && (
              <div className="mb-5 rounded-2xl border border-clay bg-surface p-4">
                <p className="text-clay text-sm">
                  "{roomGuard.name}" looks like a hotel guest. {canChargeToRoom
                    ? 'Charge it to their room, or their own tab, instead?'
                    : 'Put it on a tab in their own name instead?'}
                </p>
                <div className="space-y-2 mt-3 max-h-56 overflow-y-auto">
                  {inHouse.map(st => (
                    <button key={st.id} onClick={() => { setRoomGuard(null); setGuestChoice(st) }}
                      className="w-full h-12 px-4 rounded-xl border border-line text-left flex items-center gap-3">
                      <span className="font-bold tnum w-20 shrink-0">Rm {st.rooms?.room_number}</span>
                      <span className="truncate">{st.guests?.full_name}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => { const g = roomGuard; setRoomGuard(null); createAndPick(g.name, g.servedBy) }}
                  className="mt-3 w-full h-11 rounded-xl border border-line text-dim text-sm">
                  Not a guest — create "{roomGuard.name}" anyway
                </button>
              </div>
            )}

            <div className="text-dim mb-2">
              {creditAmount(paying) > 0
                ? 'Customer (required for credit)'
                : 'Customer (optional — for a named receipt)'}
            </div>
              <CustomerPicker branchId={staff.branch_id} canOverride={isGmOrAdmin} customers={customers} value={paying.customerId}
                onPick={id => setPaying(p => ({ ...p, customerId: id }))}
                onCreate={async (name, servedBy) => {
                  // Room-numbered accounts ("Room 203 Mr Vincent") are the
                  // habit that split guests' debt from their folio. Caught
                  // here and redirected, with an escape hatch for the rare
                  // genuine name that happens to match.
                  if (looksLikeRoom(name) && inHouse.length) {
                    setRoomGuard({ name, servedBy }); return
                  }
                  await createAndPick(name, servedBy)
                }} />
          </div>
          )}

          {/* The reason, in the place that refuses the save (18 October
              2026). It lived only on the page behind this sheet, so
              from here the app demanded a reason and showed no field to
              put one in — the person is told to do something they
              cannot reach, and the sale cannot be saved at all.

              The same state as the field on the page: typing in either
              fills both, and whichever was used is what is stored on
              every line of the sale. */}
          {needsReason && (
            <div className="mt-8 rounded-2xl border border-amber bg-surface p-4">
              <p className="font-bold text-amber">
                Recording for {new Date(date + 'T12:00:00').toLocaleDateString('en-NG',
                  { weekday: 'long', day: 'numeric', month: 'long' })} — not today
              </p>
              <p className="text-dim text-sm mt-1 mb-3">
                Say why. It is kept with the sale, so the figure it moves can be
                explained later.
              </p>
              <BackdateReasonField value={backdateReason} onChange={setBackdateReason}
                other={reasonOther} onOther={setReasonOther} />
            </div>
          )}

          {/* Asked in the payment sheet itself, not on a sheet of its own.
              A question that covers the screen takes the person away from
              the fields the answer may depend on — and if saving then
              refuses for another reason, they are standing somewhere with
              nothing to fix it with. */}
          {repeatSale && (
            <Notice tone="warn" title="Already recorded?" className="mt-6">
              <p>
                {repeatSale.qty} × {repeatSale.item} was put on this same account
                less than two minutes ago, for {naira(repeatSale.amount)}.
              </p>
              <p className="mt-2">
                If they have bought it again, go ahead. If this is the same sale
                being keyed twice, stop — it adds to what they owe twice over.
              </p>
            </Notice>
          )}

          {repeatSale ? (
            <div className="mt-4 flex flex-col gap-3">
              <button onClick={() => { setRepeatSale(null); commit(true) }} disabled={busy}
                className="w-full h-14 rounded-2xl border border-clay text-clay font-bold disabled:opacity-40">
                They bought it again — record it
              </button>
              <button onClick={() => setRepeatSale(null)} disabled={busy}
                className="w-full h-14 rounded-2xl bg-amber text-bg font-bold disabled:opacity-40">
                Stop, let me check
              </button>
            </div>
          ) : (
            <button onClick={() => commit()} disabled={busy || (needsReason && !backdateReason.trim())}
              className={`w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold disabled:opacity-40 ${
                needsReason ? 'mt-3' : 'mt-8'}`}>
              {busy ? 'Saving…'
                : paying.roomStay ? `Charge to Room ${paying.roomStay.room}`
                  : offToday ? `Save sale · ${dayShort(date)}` : 'Save sale'}
            </button>
          )}
        </Sheet>
      )}

      {receipt && (
        <Receipt lines={receipt} branchName={boot.branchName} locById={locById}
          onClose={() => setReceipt(null)} />
      )}

      {writeoff && (
        <Sheet onClose={() => setWriteoff(null)}>
          <h2 className="text-2xl font-bold">{writeoff.item.name}</h2>
          <p className="text-dim mt-1">Not a sale — this leaves stock without income.</p>
          <Row label="Reason">
            <Chip active={writeoff.kind === 'complimentary'}
              onClick={() => setWriteoff(w => ({ ...w, kind: 'complimentary' }))}>PR / free</Chip>
            <Chip active={writeoff.kind === 'damage'}
              onClick={() => setWriteoff(w => ({ ...w, kind: 'damage' }))}>Damaged</Chip>
          </Row>
          <Row label="Quantity">
            <button onClick={() => setWriteoff(w => ({ ...w, qty: Math.max(1, w.qty - 1) }))}
              className="h-14 w-14 rounded-xl bg-surface border border-line text-2xl">−</button>
            <span className="tnum text-3xl font-bold w-14 text-center">{writeoff.qty}</span>
            <button onClick={() => setWriteoff(w => ({ ...w, qty: w.qty + 1 }))}
              className="h-14 w-14 rounded-xl bg-surface border border-line text-2xl">+</button>
          </Row>
          <Row label="Value per unit">
            <input type="number" inputMode="decimal" value={writeoff.unitValue}
              onChange={e => setWriteoff(w => ({ ...w, unitValue: Number(e.target.value) }))}
              className="h-12 w-36 px-3 rounded-xl bg-surface border border-line tnum" />
          </Row>
          <Row label="Date">
            <input type="date" value={writeoff.date || date} max={todayDate}
              onChange={e => setWriteoff(w => ({ ...w, date: e.target.value }))}
              className="h-12 px-3 rounded-xl bg-surface border border-line tnum" />
          </Row>
          {writeoff.kind === 'damage' && (
            <Row label="What happened">
              <select value={writeoff.damageReason || ''}
                onChange={e => setWriteoff(w => ({ ...w, damageReason: e.target.value || null }))}
                className="h-12 px-3 rounded-xl bg-surface border border-line">
                <option value="">Pick a reason…</option>
                <option value="breakage">Breakage</option>
                <option value="expiry">Expiry</option>
                <option value="spillage">Spillage</option>
                <option value="theft">Theft</option>
                <option value="spoilage">Spoilage</option>
                <option value="other">Other</option>
              </select>
            </Row>
          )}
          <Row label={writeoff.kind === 'complimentary' ? 'Authorized by / note' : 'Note (optional)'}>
            <input value={writeoff.note || ''}
              onChange={e => setWriteoff(w => ({ ...w, note: e.target.value }))}
              placeholder={writeoff.kind === 'complimentary' ? 'e.g. approved by GM for…' : 'any detail'}
              className="h-12 w-full px-3 rounded-xl bg-surface border border-line placeholder:text-dim" />
          </Row>
          {writeoff.kind === 'damage' && !writeoff.damageReason && (
            <p className="text-dim text-sm mt-2">Pick a reason so damage can be tracked by cause.</p>
          )}
          <button onClick={commitWriteoff} disabled={busy || (writeoff.kind === 'damage' && !writeoff.damageReason)}
            className="mt-8 w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold disabled:opacity-40">
            {busy ? 'Saving…'
              : (writeoff.date || date) !== todayDate
                ? `Save write-off · ${dayShort(writeoff.date || date)}`
                : 'Save write-off'}
          </button>
        </Sheet>
      )}

      {roomCharging && (
        <RoomChargeSheet boot={boot} stockMap={stockMap} toast={toast} fromReception={isReception} deptId={locationId}
          onClose={() => setRoomCharging(false)} />
      )}

      {restaurantOrder && (
        <Sheet onClose={() => setRestaurantOrder(null)}>
          <h2 className="text-2xl font-bold">Restaurant order</h2>
          <p className="text-dim mt-1">Typed, not tracked as stock — counts toward Restaurant's takings. Use + Stock Used for anything taken from Restaurant stock with it.</p>
          <Row label="What was ordered">
            <input value={restaurantOrder.description} autoFocus
              onChange={e => setRestaurantOrder(r => ({ ...r, description: e.target.value }))}
              placeholder="e.g. Jollof Rice with Chicken"
              className="h-12 w-full px-3 rounded-xl bg-surface border border-line placeholder:text-dim" />
          </Row>
          {!priceFromStock && (
            <Row label="Price per plate">
              <input type="number" inputMode="decimal" value={restaurantOrder.unitPrice}
                onChange={e => setRestaurantOrder(r => ({ ...r, unitPrice: e.target.value }))}
                placeholder="0" className="h-12 w-36 px-3 rounded-xl bg-surface border border-line tnum" />
            </Row>
          )}

          <StockUsedField used={restaurantOrder.used || []}
            setUsed={fn => setRestaurantOrder(r => ({ ...r, used: fn(r.used || []) }))}
            items={items} stockMap={stockMap}
            restaurantId={(boot.allLocations || []).find(l => /restaurant/i.test(l.name))?.id} />

          {/* Quantity sits BELOW the stock (Chris, 29 October 2026). The
              list is what one plate takes, so the screen now reads in the
              order the work happens: what was ordered, what one plate
              takes, how many plates, what it comes to. */}
          <Row label="Quantity">
            <button onClick={() => setRestaurantOrder(r => ({ ...r, qty: Math.max(1, r.qty - 1) }))}
              className="h-14 w-14 rounded-xl bg-surface border border-line text-2xl">−</button>
            <span className="tnum text-3xl font-bold w-14 text-center">{restaurantOrder.qty}</span>
            <button onClick={() => setRestaurantOrder(r => ({ ...r, qty: r.qty + 1 }))}
              className="h-14 w-14 rounded-xl bg-surface border border-line text-2xl">+</button>
          </Row>

          {/* Stock used with this order — comes off Restaurant's stock and is
              tied to this order, for accountability. Never changes the price. */}
          {priceFromStock && (
            <div className="mt-4 rounded-xl border border-line bg-surface px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-dim">Order total &mdash; from the stock used</span>
                <span className="tnum text-2xl font-bold text-amber">
                  {naira(stockValueOf(restaurantOrder.used) * restaurantOrder.qty)}
                </span>
              </div>
              {noSaleValue(restaurantOrder) && (
                <p className="text-clay text-sm font-semibold mt-2">
                  None of this stock carries a price, so there is nothing to sell.
                  Save it as <strong>PR / Damage</strong> or <strong>Staff</strong>.
                  If a guest is paying for this, stop &mdash; the item needs a price
                  in Catalog first, or the money will not reach Restaurant.
                </p>
              )}
              <p className="text-dim text-xs mt-1">
                List what <strong>one plate</strong> takes. The app multiplies it by the
                quantity, so {restaurantOrder.qty}
                {restaurantOrder.qty === 1 ? ' plate takes' : ' plates take'} what is
                listed below{restaurantOrder.qty === 1 ? '' : ' ' + restaurantOrder.qty + ' times over'}.
              </p>
              {restaurantOrder.qty > 1 && stockValueOf(restaurantOrder.used) > 0 && (
                <p className="text-dim text-xs mt-1 tnum">
                  {naira(stockValueOf(restaurantOrder.used))} a plate &times; {restaurantOrder.qty}
                  {' '}&mdash; {(restaurantOrder.used || []).map(u =>
                    `${u.item?.name} ${Number(u.qty || 0) * restaurantOrder.qty}`).join(', ')}
                  {' '}leaves the store.
                </p>
              )}
            </div>
          )}


          <label className="block mt-4 text-dim">Order type</label>
          <div className="mt-2 flex gap-2">
            {[['standard', 'Standard'], ['pr_damage', 'PR / Damage'], ['staff', 'Staff']].map(([k, label]) => {
              const barred = k === 'standard' && noSaleValue(restaurantOrder)
              return (
              <button key={k} disabled={barred}
                onClick={() => setRestaurantOrder(r => ({ ...r, orderType: k }))}
                className={`flex-1 h-12 rounded-xl border font-semibold disabled:opacity-30 ${
                  restaurantOrder.orderType === k && !barred
                  ? 'bg-amber text-bg border-amber' : 'border-line text-dim'}`}>
                {label}
              </button>
            )})}
          </div>

          {restaurantOrder.orderType === 'staff' && (
            <>
              <p className="text-dim text-sm mt-3">
                A staff meal is free: it is not charged to anyone and does not
                count as a sale.
              </p>
              {/* Moved here from PR/Damage — it is staff meals that are
                  breakfast, lunch or dinner. */}
              <Row label="Which meal">
                <select value={restaurantOrder.prMeal || ''}
                  onChange={e => setRestaurantOrder(r => ({ ...r, prMeal: e.target.value || null }))}
                  className="h-12 px-3 rounded-xl bg-surface border border-line">
                  <option value="">Not specified</option>
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                </select>
              </Row>
              <Row label="Date">
                <input type="date" value={restaurantOrder.date || date} max={todayDate}
                  onChange={e => setRestaurantOrder(r => ({ ...r, date: e.target.value }))}
                  className="h-12 px-3 rounded-xl bg-surface border border-line tnum" />
              </Row>
            </>
          )}

          {/* PR and DAMAGE are different things and are now asked for
              separately: PR is a decision someone made, damage is a loss.
              Each gets its own detail, so the daily close can show why. */}
          {restaurantOrder.orderType === 'pr_damage' && (
            <>
              <div className="mt-3 flex gap-2">
                {[['pr', 'PR / complimentary'], ['damage', 'Damaged / lost']].map(([k, label]) => (
                  <button key={k}
                    onClick={() => setRestaurantOrder(r => ({ ...r,
                      prKind: k, damageReason: k === 'pr' ? null : (r.damageReason || 'spoilage') }))}
                    className={`flex-1 h-12 rounded-xl border font-semibold ${
                      (restaurantOrder.prKind || (restaurantOrder.damageReason ? 'damage' : 'pr')) === k
                        ? 'bg-clay text-bg border-clay' : 'border-line text-dim'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {(restaurantOrder.prKind || (restaurantOrder.damageReason ? 'damage' : 'pr')) === 'damage' ? (
                <>
                  <p className="text-dim text-sm mt-3">
                    Stock lost. Say how, so it can be checked against what was found.
                  </p>
                  <Row label="What happened">
                    <select value={restaurantOrder.damageReason || 'spoilage'}
                      onChange={e => setRestaurantOrder(r => ({ ...r, damageReason: e.target.value }))}
                      className="h-12 px-3 rounded-xl bg-surface border border-line">
                      <option value="breakage">Breakage</option>
                      <option value="expiry">Expiry</option>
                      <option value="spillage">Spillage</option>
                      <option value="spoilage">Spoilage</option>
                      <option value="theft">Theft</option>
                      <option value="other">Other</option>
                    </select>
                  </Row>
                </>
              ) : (
                <p className="text-dim text-sm mt-3">
                  Given away free. Say who it was for and who approved it.
                </p>
              )}

              <Row label="Date">
                <input type="date" value={restaurantOrder.date || date} max={todayDate}
                  onChange={e => setRestaurantOrder(r => ({ ...r, date: e.target.value }))}
                  className="h-12 px-3 rounded-xl bg-surface border border-line tnum" />
              </Row>
              <label className="block mt-4 text-dim">
                {(restaurantOrder.prKind || (restaurantOrder.damageReason ? 'damage' : 'pr')) === 'damage'
                  ? 'What happened, in your words' : 'Who it was for, and who approved it'}
              </label>
              <input value={restaurantOrder.writeoffNote}
                onChange={e => setRestaurantOrder(r => ({ ...r, writeoffNote: e.target.value }))}
                placeholder="e.g. Approved by GM Chuka"
                className="mt-2 h-12 w-full px-3 rounded-xl bg-surface border border-line placeholder:text-dim" />
            </>
          )}

          {restaurantOrder.orderType === 'staff' && (
            <>
              <label className="block mt-4 text-dim">Note (optional)</label>
              <input value={restaurantOrder.writeoffNote}
                onChange={e => setRestaurantOrder(r => ({ ...r, writeoffNote: e.target.value }))}
                className="mt-2 h-12 w-full px-3 rounded-xl bg-surface border border-line" />
            </>
          )}

          {restaurantOrder.orderType !== 'pr_damage' && !(restaurantOrder.used || []).length && (
            <p className="text-clay text-sm mt-4">
              Say what came off Restaurant stock with + Stock Used above. Every order
              has to show what left the kitchen store before it can be saved.
            </p>
          )}

          {/* PR/Damage is not blocked — a complimentary plate of food has no
              stock behind it — but it was the one way to save an order with
              no stock line at all. So it asks: list the stock, or say plainly
              that none came off. The tick is stored (migration 434) so the
              daily close can tell a declaration from a line never entered. */}
          {restaurantOrder.orderType === 'pr_damage' && !(restaurantOrder.used || []).length && (
            <label className="mt-4 flex items-start gap-3 rounded-xl border border-clay p-3">
              <input type="checkbox" checked={!!restaurantOrder.noStock}
                onChange={e => setRestaurantOrder(r => ({ ...r, noStock: e.target.checked }))}
                className="mt-1 h-5 w-5 accent-amber" />
              <span className="text-sm">
                <span className="font-semibold">Nothing came off Restaurant stock</span>
                <span className="block text-dim">
                  No drink, water or anything else that is counted. Tick this, or
                  list it with + Stock Used above.
                </span>
              </span>
            </label>
          )}

          <button
            onClick={async () => {
              const desc = restaurantOrder.description.trim()
              const qty = restaurantOrder.qty
              // Priced from stock, PER PLATE (Chris, 29 October 2026). The
              // stock listed is what one plate takes; the quantity
              // multiplies both the money and what leaves the store.
              //
              // It used to read the list as covering the whole order and
              // divide by the plates. The kitchen does not think that way
              // — it knows what one plate takes — and listing a plate
              // three times over was the step that got skipped.
              const stockValue = stockValueOf(restaurantOrder.used)
              const unitPrice = priceFromStock
                ? Math.round(stockValue * 100) / 100
                : Number(restaurantOrder.unitPrice) || 0
              // Only STANDARD is a paid order. Staff meals are a
              // benefit — free, and excluded from sales (migration 289) —
              // so they save straight away like PR, with no payment step.
              // What actually leaves the store. Multiplied here and nowhere
              // else, so the over-use warning, the save and the deduction
              // all see the same figure.
              const usedNow = priceFromStock
                ? (restaurantOrder.used || []).map(u => ({ ...u, qty: Number(u.qty || 0) * qty }))
                : (restaurantOrder.used || [])
              const restLoc = (boot.allLocations || []).find(l => /restaurant/i.test(l.name))
              // A standard order or a staff meal must say what came out of the
              // kitchen store; the button is disabled until it does, and this is
              // the backstop. PR and damage are asked rather than blocked — but
              // they must answer one way or the other.
              if (!usedNow.length && restaurantOrder.orderType !== 'pr_damage') {
                toast('Add what came off Restaurant stock with + Stock Used', 'error')
                return
              }
              if (!usedNow.length && restaurantOrder.orderType === 'pr_damage'
                  && !restaurantOrder.noStock) {
                toast('List the stock, or tick that nothing came off Restaurant stock', 'error')
                return
              }
              const overUsed = usedNow.filter(u => u.qty > (stockMap[`${u.item.id}:${restLoc?.id}`] ?? 0))
              if (overUsed.length && !window.confirm(
                    `More than Restaurant stock shows:\n\n${overUsed.map(u => `${u.item.name} (${stockMap[`${u.item.id}:${restLoc?.id}`] ?? 0} left, using ${u.qty})`).join('\n')}\n\nRecord anyway?`)) return
              if (restaurantOrder.orderType === 'standard') {
                addTypedOrder({ description: desc, qty, unitPrice, orderType: restaurantOrder.orderType,
                                writeoffNote: restaurantOrder.writeoffNote, stockUsed: usedNow })
                setRestaurantOrder(null)
                return
              }
              setWriteoffBusy(true)
              try {
                const restaurant = (boot.allLocations || []).find(l => /restaurant/i.test(l.name))
                if (!canRecordAt(restaurant?.id)) {
                  toast('View only — you can record only at your own departments', 'error')
                  setWriteoffBusy(false)
                  return
                }
                await saveRestaurantWriteoff({
                  staff, locationId: restaurant?.id, businessDate: restaurantOrder.date || date,
                  description: desc, qty, unitPrice,
                  orderType: restaurantOrder.orderType, damageReason: restaurantOrder.damageReason,
                  writeoffNote: restaurantOrder.writeoffNote, prMeal: restaurantOrder.prMeal,
                  stockUsed: usedNow,
                  noStockDeclared: !usedNow.length && !!restaurantOrder.noStock,
                })
                toast(restaurantOrder.orderType === 'staff'
                  ? 'Staff meal recorded' : 'Recorded — not paid for', 'success')
                setRestaurantOrder(null); refresh()
              } catch (e) {
                if (e.partial) { setRestaurantOrder(null); refresh() }
                toast((e.partial ? '' : 'Not saved: ') + e.message, 'error')
              }
              setWriteoffBusy(false)
            }}
            disabled={writeoffBusy || !restaurantOrder.description.trim()
              // Nothing to sell: saveable as PR or a staff meal, never as a sale.
              || (noSaleValue(restaurantOrder) && restaurantOrder.orderType === 'standard')
              || !(priceFromStock
                    ? (stockValueOf(restaurantOrder.used) > 0 || noSaleValue(restaurantOrder))
                    : Number(restaurantOrder.unitPrice))
              || (!(restaurantOrder.used || []).length
                  && (restaurantOrder.orderType !== 'pr_damage' || !restaurantOrder.noStock))}
            className="mt-8 w-full h-16 rounded-2xl bg-amber text-bg text-xl font-bold disabled:opacity-40">
            {writeoffBusy ? 'Saving…'
              : restaurantOrder.orderType === 'standard' ? 'Add to basket'
              : restaurantOrder.orderType === 'staff' ? 'Save staff meal'
              : 'Save — not paid for'}
          </button>
        </Sheet>
      )}

      {deleteConfirm && (
        <Sheet onClose={() => setDeleteConfirm(null)}>
          <h2 className="text-2xl font-bold">Delete this entry?</h2>
          <p className="text-dim mt-2">
            {itemById[deleteConfirm.stock_item_id]?.name || deleteConfirm.description || 'This entry'}
            {' — '}{deleteConfirm.qty} × {naira(deleteConfirm.unit_price)}
          </p>
          <p className="text-dim text-sm mt-1">
            For training records only — this cannot be undone.
          </p>
          <button onClick={doDeleteEntry} disabled={deleteBusy}
            className="mt-6 w-full h-14 rounded-2xl bg-clay text-bg text-lg font-bold disabled:opacity-40">
            {deleteBusy ? 'Deleting…' : 'Delete'}
          </button>
          <button onClick={() => setDeleteConfirm(null)} className="mt-3 w-full h-12 text-dim">Cancel</button>
        </Sheet>
      )}
    </div>
  )
}

// gap-3 and shrink-0 on the amount: at 320px a long label used to wrap
// under itself while its figure stayed on the first line, which read as
// two different rows. The label wraps, the money never moves.
function Line({ label, value, tone = '' }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-dim min-w-0">{label}</span>
      <span className={`tnum shrink-0 ${tone}`}>{naira(value)}</span>
    </div>
  )
}


function Allocation({ total, paying, methods, onCredit }) {
  const allocated = paying.split
    ? Object.values(paying.split).reduce((s, v) => s + Number(v || 0), 0)
    : total
  const diff = Number((total - allocated).toFixed(2))
  return (
    <div className="mt-6 rounded-xl border border-line bg-surface p-4 space-y-1 text-sm">
      <div className="flex justify-between">
        <span className="text-dim">Expected</span>
        <span className="tnum font-bold">{naira(total)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-dim">Allocated</span>
        <span className="tnum">{naira(allocated)}</span>
      </div>
      <div className="flex justify-between pt-1 border-t border-line">
        <span className="text-dim">Difference</span>
        <span className={`tnum font-bold ${Math.abs(diff) < 0.005 ? 'text-leaf' : 'text-clay'}`}>
          {naira(diff)}
        </span>
      </div>
      {diff > 0.005 && methods.includes('credit') && (
        <button onClick={onCredit}
          className="mt-3 w-full h-11 rounded-xl border border-amber text-amber font-semibold">
          Post {naira(diff)} as credit
        </button>
      )}
    </div>
  )
}

function Sheet({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-bg h-[100dvh] flex flex-col">
      <div className="p-5 flex-1 min-h-0 overflow-y-auto">
        <button onClick={onClose} className="text-dim">Back</button>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  )
}
function Row({ label, children }) {
  return (
    <div className="mt-6">
      <div className="text-dim mb-2">{label}</div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}
function Chip({ active, onClick, children }) {
  return <button onClick={onClick}
    className={`h-12 px-4 rounded-xl border font-semibold ${active
      ? 'bg-amber text-bg border-amber' : 'border-line text-ink'}`}>{children}</button>
}
function SplitCheck({ split, total }) {
  const entered = Object.values(split).reduce((s, v) => s + Number(v || 0), 0)
  const diff = total - entered
  if (Math.abs(diff) < 0.01) return <p className="text-leaf">Split matches the total.</p>
  return <p className="text-clay tnum">
    {diff > 0 ? naira(diff) + ' left to allocate' : naira(-diff) + ' over the total'}
  </p>
}
